// src/pages/Home.tsx
import React, { useState, useEffect, useMemo, useRef, Fragment, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useDataCache } from '../context/DataCacheContext';
import { 
  IoCloseOutline, IoAddOutline, IoPencilOutline, IoSparklesOutline, 
  IoArchiveOutline, IoTrashOutline, IoSettingsOutline, IoFunnelOutline, 
  IoCheckmarkOutline, IoChevronDownOutline, IoChevronForwardOutline, 
  IoDocumentTextOutline, IoStarSharp, IoStarOutline, IoChevronUpOutline, 
  IoToggleOutline, IoSearchOutline, IoBriefcase, IoCheckmarkCircleOutline, 
  IoListOutline 
} from 'react-icons/io5';
import { useOutletContext } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { PAGE_HEADER_HEIGHT } from '../constants/layout';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { useCachedData } from '../hooks/useCachedData';
import { createLogger } from '../utils/logger';
import { useBannerAutoRead } from '../hooks/useBannerAutoRead';
import ReviewModal from '../components/ReviewModal';
import { FilterItem } from '../types/notes';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate, formatDateOnly } from '../utils/dateUtils';
import useUserTimezone from '../hooks/useUserTimezone';
import { getDateTags } from '../utils/dateTags';
import MDEditor, { commands, ICommand } from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import '../styles/markdown-editor.css';
import TagsDropdown from '../components/EnhancedFilterModal/TagsDropdown';
import { extractTags } from '../utils/tagUtils';
import { UnifiedSidePanel } from '../components/UnifiedSidePanel';
import { CareerCardPanel } from '../components/CareerCardPanel';
import { Note, Review, Item, ItemUpdate } from '../types/items';
import { ReviewsSettings, ViewMode } from '../components/ReviewsSettings';
import { CareerCard as CareerCardType } from '../types/careerCard';
import CardTypeModal from '../components/CardTypeModal';
import { CardType } from '../types';
import { useToast } from '../context/ToastContext';
import { Modal } from '../components/Modal';
import { ListItem, Box, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { XP_AMOUNTS } from '../constants/xpAwards';
import { awardXP } from '../utils/xpAwards';
import { handleNetworkError } from '../utils/networkUtils';
import { useLocation } from 'react-router-dom';
import { IMPACT_LEVELS, ENGAGEMENT_LEVELS } from '../constants/impactLevels';
import { useEditState } from '../hooks/useEditState';
import { Temporal } from '@js-temporal/polyfill';
import { useAuthState } from '../hooks/useAuthState';
import { Dialog, Transition } from '@headlessui/react';
import ToDoModal from '../components/ToDoModal';
import useTouchScroll from '../hooks/useTouchScroll';
import useBottomUrlBarDetection from '../hooks/useBottomUrlBarDetection';
import OnboardingBanner from '../components/OnboardingBanner';
import { useAuth } from '../context/AuthContext';
import { useUXFeatures, useBusinessMetrics, useAppConfig } from '../hooks/useStatsigFeatures';
import { NotificationEvents } from '../services/notificationsV2Integration';
import { DatabaseBanner } from '../components/notifications/DatabaseBanner';
import { useAdminSettings } from '../context/AdminSettingsContext';
import { useCareerFocusFeatures } from '../hooks/useStatsigFeatures';
import { FocusModeSlider } from '../components/CareerFocus';
import { useActiveCareerFocus } from '../hooks/useCareerFocus';

const logger = createLogger({ module: 'Home', level: 'info' });

interface CareerCard extends CareerCardType {
  title: string;
  type: string;
  custom_tags?: string[];
  date_tags?: string[];
  is_pinned: boolean;  // Changed from optional to required
}

interface CombinedItem {
  id: string;
  type: 'review' | 'note' | 'card';
  title: string;
  content: string;
  custom_tags?: string[];
  date_tags?: string[];
  created_at: string;
  is_pinned: boolean; // Add this field
  review?: Review;
  note?: Note;
  card?: CareerCard;
}

interface PageHeaderProps {
  title: string;
  primaryButton?: React.ReactNode;
}

interface TypeVisibility {
  reviews: boolean;
  notes: boolean;
  careerCards: boolean;
}

export type SortOption = 'date' | 'title';

interface UnifiedHomeSettings {
  viewMode: ViewMode;
  sortBy: SortOption;
  groupByType: boolean;
  itemsPerPage: number;
  scrollMode: 'pagination' | 'infinite';
}

const HOME_CACHE_KEYS = {
  ...CACHE_KEYS,
  USER_SETTINGS: 'user-settings' as const,
} as const;

const defaultSettings: UnifiedHomeSettings = {
  viewMode: 'condensed',
  sortBy: 'date',
  groupByType: false,
  itemsPerPage: 20,
  scrollMode: 'infinite'
};

interface GroupCounts {
  reviews: number;
  notes: number;
  careerCards: number;
}

interface GroupCountResult {
  item_type: 'review' | 'note' | 'card';
  count: number;
}

interface ItemMetadata {
  totalCounts?: {
    reviews: number;
    notes: number;
    careerCards: number;
  };
  totalCount?: number;
  hasMore: {
    reviews?: boolean;
    notes?: boolean;
    careerCards?: boolean;
  } | boolean;
}

interface ContentTypeCount {
  item_type: 'review' | 'note' | 'card';
  count: number;
}

// Function to clean up markdown for preview
const cleanMarkdown = (text: string) => {
  return text
    .replace(/^[\s-•*]*$/gm, '') // Remove lines with only whitespace, dash, bullet, or asterisk
    .replace(/^[•*-]\s*/gm, '') // Remove bullet points, asterisks, and dashes at the start of lines
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold syntax
    .replace(/\*(.*?)\*/g, '$1') // Remove italic syntax
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove link syntax
    .replace(/#{1,6}\s?/g, '') // Remove heading syntax
    .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // Remove code syntax
    .replace(/\n{3,}/g, '\n\n') // Replace 3 or more newlines with 2
    .trim();
};

// Function to format output style for display
const formatOutputStyle = (style: string): string => {
  return style.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Function to render markdown
const renderMarkdown = (content: string) => {
  const cleanedContent = content
    .replace(/^[\s-•]*$/gm, '') // Remove lines with only whitespace, dash, or bullet
    .replace(/\n{3,}/g, '\n\n') // Replace 3 or more newlines with 2
    .replace(/^•\s*$/gm, ''); // Remove lines that only contain a bullet point

  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        h1: ({ ...props }) => <h1 className="text-2xl font-bold my-4" {...props} />,
        h2: ({ ...props }) => <h2 className="text-xl font-bold my-3" {...props} />,
        h3: ({ ...props }) => <h3 className="text-lg font-bold my-2" {...props} />,
        p: ({ ...props }) => <p className="my-2" {...props} />,
        ul: ({ ...props }) => <ul className="list-disc list-inside my-2" {...props} />,
        ol: ({ ...props }) => <ol className="list-decimal list-inside my-2" {...props} />,
        li: ({ children, ...props }) => {
          // Check if the list item is empty (contains only whitespace)
          const isEmpty = React.Children.toArray(children).every(
            child => typeof child === 'string' && child.trim() === ''
          );
          return isEmpty ? null : <li className="ml-4" {...props}>{children}</li>;
        },
        a: ({ ...props }) => <a className="text-blue-500 hover:underline dark:text-blue-400" {...props} />,
      }}
    >
      {cleanedContent}
    </ReactMarkdown>
  );
};

// Add this before the Reviews component
const underlineCommand: ICommand = {
  name: 'underline',
  keyCommand: 'underline',
  buttonProps: { 'aria-label': 'Add underline text' },
  icon: (
    <svg width="12" height="12" viewBox="0 0 20 20">
      <path fill="currentColor" d="M10 17a6 6 0 0 0 6-6V3h-2.5v8a3.5 3.5 0 0 1-7 0V3H4v8a6 6 0 0 0 6 6zm-7 2h14v1H3z" />
    </svg>
  ),
  execute: (state, api) => {
    let modifyText = `<u>${state.selectedText}</u>`;
    if (!state.selectedText) {
      modifyText = '<u>underlined text</u>';
    }
    api.replaceSelection(modifyText);
  },
};

// Add before the Reviews component
const ARCHIVE_TAG = 'Archived';

// Add a helper function to format tag display
const formatTagDisplay = (tag: string): string => {
  return tag.charAt(0).toUpperCase() + tag.slice(1);
};

// Add constants
const ACTIVE_TAG = 'Active';

// Add type for the accumulator
interface TagAccumulator {
  [key: string]: string[];
}

// Add type for the items
interface GroupedItems {
  reviews: CombinedItem[];
  notes: CombinedItem[];
  careerCards: CombinedItem[];  // Changed from 'cards' to 'careerCards'
}

// Add type for the item handlers
interface ItemHandlers {
  handleItemClick: (item: CombinedItem) => void;
  handleItemSave: (item: CombinedItem) => Promise<void>;
  handleItemDelete: (item: CombinedItem) => Promise<void>;
}

// Add after other interfaces
interface SelectedItems {
  [key: string]: boolean;
}

// Add this helper function near the top of the file with other utility functions
const getSelectedItemsCounts = (selectedItems: SelectedItems, items: CombinedItem[] | GroupedItems) => {
  const counts = {
    reviews: 0,
    notes: 0,
    careerCards: 0  // Changed from 'cards' to 'careerCards'
  };

  // Helper to process a single item
  const countItem = (item: CombinedItem) => {
    if (selectedItems[item.id]) {
      const type = item.type === 'review' 
        ? 'reviews' 
        : item.type === 'note' 
          ? 'notes' 
          : 'careerCards';  // Changed from 'cards' to 'careerCards'
      counts[type]++;
    }
  };

  // Process items based on whether they're grouped or not
  if (Array.isArray(items)) {
    items.forEach(countItem);
  } else {
    Object.values(items).flat().forEach(countItem);
  }

  return counts;
};

// Add this helper function to check if all selected items are archived
const areAllSelectedItemsArchived = (selectedItems: SelectedItems, items: CombinedItem[] | GroupedItems) => {
  const allItems = Array.isArray(items) ? items : Object.values(items).flat();
  const selectedItemsList = allItems.filter(item => selectedItems[item.id]);
  return selectedItemsList.every(item => item.custom_tags?.includes(ARCHIVE_TAG));
};

// Add this type for tracking the last archive action
interface ArchiveAction {
  items: CombinedItem[];
  wasArchiving: boolean;
}

// Add this interface near the top with other interfaces
interface SuggestedCardPreview {
  cardid: string;
  card_data: {
    title: string;
    type: string;
    overview: string;
    custom_tags: string[];
    date_tags: string[];
    [key: string]: any;
  };
  card_version: number;
}

// Add this helper function near the top of the file
const focusNextItem = (currentId: string, direction: 'up' | 'down') => {
  const items = document.querySelectorAll('[role="button"][tabindex="0"]');
  const itemArray = Array.from(items);
  const currentIndex = itemArray.findIndex(item => item.id === currentId);
  
  if (currentIndex === -1) return;
  
  let nextIndex;
  if (direction === 'down') {
    nextIndex = currentIndex + 1 >= itemArray.length ? 0 : currentIndex + 1;
  } else {
    nextIndex = currentIndex - 1 < 0 ? itemArray.length - 1 : currentIndex - 1;
  }
  
  (itemArray[nextIndex] as HTMLElement).focus();
};

// Add useBreakpoint hook
const useBreakpoint = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkBreakpoint = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, []);

  return isMobile;
};

// Add this before the Home component
const useInitialSettings = (ssrSettings?: any) => {
  let getData: any = null;
  
  // Safely try to get the useDataCache hook
  try {
    const dataCacheContext = useDataCache();
    getData = dataCacheContext.getData;
  } catch (error) {
    // If DataCacheProvider is not available, we'll use fallback logic
    console.warn('DataCacheProvider not available, using fallback settings:', error);
  }
  
  // Use SSR settings if provided, otherwise try to get from cache (with fallback)
  let settingsSource = ssrSettings?.unified_home_settings;
  
  if (!settingsSource && getData) {
    try {
      settingsSource = getData<{ unified_home_settings: UnifiedHomeSettings }>(HOME_CACHE_KEYS.USER_SETTINGS)?.data?.unified_home_settings;
    } catch (error) {
      console.warn('Failed to get settings from cache, using defaults:', error);
    }
  }
  
  return {
    viewMode: settingsSource?.viewMode || defaultSettings.viewMode,
    sortBy: settingsSource?.sortBy || defaultSettings.sortBy,
    groupByType: settingsSource?.groupByType ?? defaultSettings.groupByType,
    itemsPerPage: settingsSource?.itemsPerPage || defaultSettings.itemsPerPage,
    scrollMode: settingsSource?.scrollMode || defaultSettings.scrollMode
  };
};

/**
 * Home component manages the main interface including the list and detail panels.
 * 
 * Panel Animation Behavior:
 * The side panels (UnifiedSidePanel & CareerCardPanel) use a specific animation pattern:
 * - Opening from empty state: Panel slides in from right
 * - Switching between panels: No slide animation
 * - Closing: Always slides out to right
 * 
 * This is controlled by tracking wasPanelOpen state, which indicates if we're
 * switching between panels (true) or opening from empty state (false)
 */
interface HomeProps {
  initialNotes?: any[];
  initialCards?: any[];
  initialReviews?: any[];
  initialSettings?: { unified_home_settings?: UnifiedHomeSettings };
  initialFeatureFlags?: Record<string, boolean>;
  initialExperiments?: Record<string, any>;
}

const Home: React.FC<HomeProps> = ({
  initialNotes: ssrNotes,
  initialCards: ssrCards,
  initialReviews: ssrReviews,
  initialSettings: ssrSettings,
  initialFeatureFlags = {} as Record<string, boolean>,
  initialExperiments = {} as Record<string, any>
}) => {
  // IMPORTANT: ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Use the useUserTimezone hook to get access to formatWithTimezone
  const { formatWithTimezone, formatDateOnlyWithTimezone } = useUserTimezone();
  
  // Add the scrollContainerRef right at the top level
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Auto-mark banner notifications as read when landing on home page
  useBannerAutoRead();
  
  // Apply touch scroll handling
  useTouchScroll(scrollContainerRef);
  
  // Add URL bar detection
  const { isBottomUrlBarVisible } = useBottomUrlBarDetection();
  // Replace isMobile detection with responsive window width check
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // Add journey ID refs for tracking
  // const homeJourneyIdRef = useRef<string>('');
  // const noteJourneyIdRef = useRef<string>('');
  // const cardJourneyIdRef = useRef<string>('');
  // const reviewJourneyIdRef = useRef<string>('');
  const pageLoadStartTimeRef = useRef<number>(performance.now());

  const initialSettings = useInitialSettings(ssrSettings);
  const [viewMode, setViewMode] = useState<ViewMode>(initialSettings.viewMode);
  const [sortBy, setSortBy] = useState<SortOption>(initialSettings.sortBy);
  const [groupByType, setGroupByType] = useState(initialSettings.groupByType);
  const [itemsPerPage, setItemsPerPage] = useState(initialSettings.itemsPerPage);
  const [scrollMode, setScrollMode] = useState<'pagination' | 'infinite'>(initialSettings.scrollMode);

  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isNewReviewModalOpen, setIsNewReviewModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const [editedReviewText, setEditedReviewText] = useState('');
  const [editedCustomTags, setEditedCustomTags] = useState<string[]>([]);
  const [editedDateTags, setEditedDateTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [allUserTags, setAllUserTags] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  const { openReviewId } = useOutletContext<{ openReviewId?: string }>() || {};
  const [typeVisibility, setTypeVisibility] = useState<TypeVisibility>({
    reviews: true,
    notes: true,
    careerCards: true  // Changed from 'cards' to 'careerCards'
  });
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [selectedCard, setSelectedCard] = useState<CareerCard | null>(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean>>({});
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const createDropdownRef = useRef<HTMLDivElement>(null);
  const [listWidth, setListWidth] = useState(400);
  const [wasPanelOpen, setWasPanelOpen] = useState(false);
  const [isCardTypeModalOpen, setIsCardTypeModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({});
  const [lastArchiveAction, setLastArchiveAction] = useState<ArchiveAction | null>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isCareerImpactOpen, setIsCareerImpactOpen] = useState(false);
  const [isEngagementLevelOpen, setIsEngagementLevelOpen] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const newNoteButtonRef = useRef<HTMLButtonElement>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [careerCards, setCareerCards] = useState<CareerCard[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const {
    noteEditState,
    reviewEditState,
    cardEditState,
    setNoteEditState,
    setReviewEditState,
    setCardEditState,
    getCurrentEditState,
    clearAllEditStates,
    hasAnyUnsavedChanges
  } = useEditState();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [targetItem, setTargetItem] = useState<CombinedItem | null>(null);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [groupCounts, setGroupCounts] = useState<GroupCounts>({
    reviews: 0,
    notes: 0,
    careerCards: 0
  });

  // Add useAuthState hook
  const { user, isAuthenticated, isLoading: authLoading } = useAuthState();
  const { onboardingComplete } = useAuth();

  // Add Statsig hooks for feature flags and analytics
  const { features: uxFeatures } = useUXFeatures();
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  const config = useAppConfig();
  const { features: careerFocusFeatures } = useCareerFocusFeatures();
  const { focus: activeCareerFocus } = useActiveCareerFocus();

  // Add effect to update isMobile state when window is resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track page load and user journey
  useEffect(() => {
    if (user && !authLoading) {
      trackUserJourney('home_page_loaded', {
        enhanced_dashboard: uxFeatures.enhancedDashboard,
        mobile_optimizations: uxFeatures.mobileOptimizations,
        items_per_page: config.maxNotificationsPerHour,
        view_mode: viewMode,
        group_by_type: groupByType
      });
    }
  }, [user, authLoading, uxFeatures.enhancedDashboard, uxFeatures.mobileOptimizations, config.maxNotificationsPerHour, viewMode, groupByType, trackUserJourney]);
  
  // Admin settings for debug UI controls
  const adminSettings = useAdminSettings();
  
  // Use data cache with safe fallback - no early returns to avoid hooks rule violations
  let dataCacheContext: any = null;
  try {
    dataCacheContext = useDataCache();
  } catch (error) {
    console.error('DataCacheProvider not available in Home component:', error);
    // Instead of early return, we'll provide a minimal fallback context
    dataCacheContext = {
      invalidateCache: () => Promise.resolve(),
      getData: () => Promise.resolve(null),
      setData: () => Promise.resolve(),
      getWithOfflineSupport: () => Promise.resolve(null),
      setWithOfflineSupport: () => Promise.resolve(),
      syncOfflineOperations: () => Promise.resolve(),
      getOfflineOperationsCount: () => 0
    };
  }

  const { 
    invalidateCache, 
    getData, 
    setData,
    // Add offline-first methods
    getWithOfflineSupport,
    setWithOfflineSupport,
    syncOfflineOperations,
    getOfflineOperationsCount
  } = dataCacheContext;

  // Add To-Do modal state
  const [isToDoModalOpen, setIsToDoModalOpen] = useState(false);
  const [activeToDos, setActiveToDos] = useState<Array<{card: any, todos: any[]}>>([]);
  const [activeFollowUps, setActiveFollowUps] = useState<Array<{card: any, followUps: any[]}>>([]);
  const [isLoadingToDos, setIsLoadingToDos] = useState(false);

  // Add offline status indicator
  const [offlineOpsCount, setOfflineOpsCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // First define all our data hooks so refresh functions are available
  const { 
    data: unifiedData = { reviews: [], notes: [], careerCards: [] }, 
    loading: unifiedLoading, 
    error: unifiedError, 
    refresh: refreshUnifiedData 
  } = useCachedData<{
    reviews: Review[];
    notes: Note[];
    careerCards: CareerCard[];
  }>(
    async () => {
      // Single unified database call with Promise.all for optimal performance
      const [reviewsResult, notesResult, cardsResult] = await Promise.all([
        supabase
          .from('reviews')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('notes')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('career_cards')
          .select('*')
          .order('created_at', { ascending: false })
      ]);
      
      if (reviewsResult.error) throw reviewsResult.error;
      if (notesResult.error) throw notesResult.error;
      if (cardsResult.error) throw cardsResult.error;
      
      return {
        reviews: reviewsResult.data || [],
        notes: notesResult.data || [],
        careerCards: cardsResult.data || []  // Preserve original structure
      };
    },
    {
      cacheKey: 'user-unified-content',
      maxAge: CACHE_DURATIONS.MEDIUM,
      useHybridCache: true, // Enable hybrid cache for enhanced performance
      initialData: {
        reviews: ssrReviews || [],
        notes: ssrNotes || [],
        careerCards: ssrCards || []
      },
      onSuccess: (data) => {
        logger.debug('Unified content cache updated:', { 
          reviewsCount: data.reviews.length,
          notesCount: data.notes.length,
          careerCardsCount: data.careerCards.length
        });
      },
      onError: (error) => {
        logger.error('Error fetching unified content:', error);
      }
    }
  );

  // Extract individual data arrays from unified response
  const cachedReviews = unifiedData?.reviews || [];
  const cachedNotes = unifiedData?.notes || [];
  const cachedCards = unifiedData?.careerCards || [];

  // Maintain backward compatibility for loading and error states
  const reviewsLoading = unifiedLoading;
  const notesLoading = unifiedLoading;
  const cardsLoading = unifiedLoading;
  const reviewsError = unifiedError;
  const notesError = unifiedError;
  const cardsError = unifiedError;

  // Create individual refresh functions that call the unified refresh
  const refreshReviews = refreshUnifiedData;
  const refreshNotes = refreshUnifiedData;
  const refreshCards = refreshUnifiedData;

  // Modify the data loading function to use the tracking flag
  const fetchAndInitializeData = async (): Promise<(() => void) | void> => {
    const dataLoadStartTime = performance.now();
    
    // Skip if auth is still loading or user is not authenticated
    if (authLoading || !isAuthenticated || !user) return;
    
    let mounted = true;
    
    try {
      // Use the unified refresh function instead of three separate calls
      await refreshUnifiedData();
      
      if (!mounted) return;
      
      // Now we can safely set state using the cached data which was updated by the refresh
      setReviews(cachedReviews || []);
      setNotes(cachedNotes || []);
      setCareerCards(cachedCards || []);
      
      // After first successful data load, set isInitialLoad to false
      setIsInitialLoad(false);
      
      // Track successful data load only once
      trackFeatureUsage('home_data_load', 'complete', {
        load_time_ms: Math.round(performance.now() - dataLoadStartTime),
        first_paint_ms: Math.round(
          performance.getEntriesByType('paint')
            .find(entry => entry.name === 'first-paint')?.startTime || performance.now()
        )
      });
      
      // Complete journey step
      trackUserJourney('data_loaded', {
        reviews_count: cachedReviews?.length || 0,
        notes_count: cachedNotes?.length || 0,
        cards_count: cachedCards?.length || 0
      });
    } catch (error) {
      if (!mounted) return;
      console.error('Failed to initialize data:', error);
      
      // Set isInitialLoad to false even on error to avoid infinite loading state
      setIsInitialLoad(false);
      
      // Track failed data load only once
      trackFeatureUsage('home_data_load', 'error', {
        load_time_ms: Math.round(performance.now() - dataLoadStartTime),
        first_paint_ms: Math.round(
          performance.getEntriesByType('paint')
            .find(entry => entry.name === 'first-paint')?.startTime || performance.now()
        ),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      // Record error in journey
      trackUserJourney('data_load_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
    
    return () => {
      mounted = false;
    };
  };

  // Add effect to fetch data when auth changes
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      // Check if this is our first render after a non-MFA login
      // If so, data should already be initialized
      const isDataAlreadyInitialized = sessionStorage.getItem('user_data_initialized') === 'true';
      
      if (isDataAlreadyInitialized) {
        // Clear the flag to prevent this from running again
        sessionStorage.removeItem('user_data_initialized');
        logger.debug('Home: Using pre-initialized data from auth context');
      } else {
        // Normal flow - fetch data
        fetchAndInitializeData();
      }
      
      // CRITICAL: Always check onboarding status to ensure UI elements correctly display
      logger.debug('Home: Checking onboarding status', { onboardingComplete });
    }
  }, [authLoading, isAuthenticated, user, onboardingComplete]);

  // Add effect to load data
  useEffect(() => {
    // Only fetch if auth is not loading - data fetch is handled in the auth effect above
    if (!authLoading) {
      fetchAndInitializeData();
    }
  }, []);

  // Track page load and view
  useEffect(() => {
    // Create a flag to ensure we only track once
    let hasTracked = false;
    
    // Track page view
    trackFeatureUsage('home_page', 'view');
    
    // Track page load performance only once
    if (!hasTracked) {
      hasTracked = true;
      const loadTime = performance.now() - pageLoadStartTimeRef.current;
      trackFeatureUsage('home_page_load', 'complete', {
        load_time_ms: Math.round(loadTime),
        first_paint_ms: Math.round(
          performance.getEntriesByType('paint')
            .find(entry => entry.name === 'first-paint')?.startTime || loadTime
        )
      });
    }
    
    return () => {
      // Track page exit
      trackUserJourney('page_exit');
    };
  }, []); // Empty dependency array to ensure it only runs once

  // Define handleSessionRecovery here
  const handleSessionRecovery = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        logger.error('Session recovery error:', error);
        return false;
      }
      return !!session;
    } catch (error) {
      logger.error('Session recovery failed:', error);
      return false;
    }
  };

  // Add session recovery effect
  useEffect(() => {
    handleSessionRecovery().catch(error => {
      logger.error('Session recovery failed:', error);
    });
  }, []);

  // Add a cache for group counts
  const groupCountsCache = useRef<{
    data: GroupCounts | null;
    timestamp: number;
    query: string;
    tags: string[];
    archived: boolean;
  }>({
    data: null,
    timestamp: 0,
    query: '',
    tags: [],
    archived: false
  });

  // Add effect to handle infinite scroll
  useEffect(() => {
    if (scrollMode !== 'infinite') return;

    const handleScroll = () => {
      if (!listContainerRef.current || isLoadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current;
      const scrollThreshold = 100; // pixels from bottom to trigger load more

      if (scrollHeight - (scrollTop + clientHeight) < scrollThreshold) {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
      }
    };

    const listContainer = listContainerRef.current;
    if (listContainer) {
      listContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (listContainer) {
        listContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollMode, isLoadingMore, totalPages]);

  // Add click outside handler for actions dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Then move all the useCachedData hooks before the isInitialLoad effect
  // Note: These hooks are duplicates of the ones defined earlier in the file, so they're being removed to fix linter errors

  // Add effect to monitor cache state
  useEffect(() => {
    logger.debug('Career cards cache state:', { 
      loading: cardsLoading,
      error: cardsError,
      dataLength: cachedCards?.length || 0
    });
  }, [cardsLoading, cardsError, cachedCards]);

  // Now add the isInitialLoad effect after all the data hooks
  useEffect(() => {
    if (!reviewsLoading && !notesLoading && !cardsLoading) {
      setIsInitialLoad(false);
    }
  }, [reviewsLoading, notesLoading, cardsLoading]);

  // Then continue with the rest of the useEffects for cached data - optimize performance
  useEffect(() => {
    if (!reviewsLoading && cachedReviews) {
      setReviews(prev => {
        if (isInitialLoad) return cachedReviews;
        // Use length and timestamp checks instead of expensive JSON.stringify
        if (prev.length !== cachedReviews.length) return cachedReviews;
        // Quick check: compare first and last items if arrays have same length
        if (prev.length > 0 && cachedReviews.length > 0) {
          const firstChanged = prev[0]?.id !== cachedReviews[0]?.id || prev[0]?.updated_at !== cachedReviews[0]?.updated_at;
          const lastChanged = prev[prev.length - 1]?.id !== cachedReviews[cachedReviews.length - 1]?.id;
          if (firstChanged || lastChanged) return cachedReviews;
        }
        return prev;
      });
    }
  }, [cachedReviews, reviewsLoading, isInitialLoad]);

  useEffect(() => {
    if (!notesLoading && cachedNotes) {
      setNotes(prev => {
        if (isInitialLoad) return cachedNotes;
        // Use length and timestamp checks instead of expensive JSON.stringify
        if (prev.length !== cachedNotes.length) return cachedNotes;
        // Quick check: compare first and last items if arrays have same length
        if (prev.length > 0 && cachedNotes.length > 0) {
          const firstChanged = prev[0]?.id !== cachedNotes[0]?.id || prev[0]?.created_at !== cachedNotes[0]?.created_at;
          const lastChanged = prev[prev.length - 1]?.id !== cachedNotes[cachedNotes.length - 1]?.id;
          if (firstChanged || lastChanged) return cachedNotes;
        }
        return prev;
      });
    }
  }, [cachedNotes, notesLoading, isInitialLoad]);

  useEffect(() => {
    if (!cardsLoading && cachedCards) {
      setCareerCards(prev => {
        if (isInitialLoad) return cachedCards;
        // Use length and timestamp checks instead of expensive JSON.stringify
        if (prev.length !== cachedCards.length) return cachedCards;
        // Quick check: compare first and last items if arrays have same length
        if (prev.length > 0 && cachedCards.length > 0) {
          const firstChanged = prev[0]?.cardid !== cachedCards[0]?.cardid || prev[0]?.updated_at !== cachedCards[0]?.updated_at;
          const lastChanged = prev[prev.length - 1]?.cardid !== cachedCards[cachedCards.length - 1]?.cardid;
          if (firstChanged || lastChanged) return cachedCards;
        }
        return prev;
      });
    }
  }, [cachedCards, cardsLoading, isInitialLoad]);

  // Update the useEffect for keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Handle Ctrl/Cmd + K
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setShowArchived(prev => !prev);
      }
      
      // Handle Ctrl/Cmd + M
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
        event.preventDefault();
        setIsCreateDropdownOpen(prev => {
          if (!prev) {
            setTimeout(() => {
              newNoteButtonRef.current?.focus();
            }, 50);
          }
          return !prev;
        });
      }

      // Handle Escape key
      if (event.key === 'Escape') {
        // Close dropdown first if it's open
        if (isCreateDropdownOpen) {
          setIsCreateDropdownOpen(false);
          return;
        }
        
        // Then handle other panels
        if (selectedReview) {
          setSelectedReview(null);
        }
        if (selectedNote) {
          setSelectedNote(null);
        }
        if (selectedCard) {
          setSelectedCard(null);
        }
        if (isCardTypeModalOpen) {
          setIsCardTypeModalOpen(false);
        }
        if (isNewReviewModalOpen) {
          setIsNewReviewModalOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    isCreateDropdownOpen,
    selectedReview,
    selectedNote,
    selectedCard,
    isCardTypeModalOpen,
    isNewReviewModalOpen
  ]);

  // Update the handleItemSelection function:
  const handleItemSelection = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation(); // Prevent item click from triggering
    setSelectedItems(prev => {
      const newState = { ...prev };
      if (prev[itemId]) {
        // If item was selected, remove it from the selection
        delete newState[itemId];
      } else {
        // If item wasn't selected, add it to the selection
        newState[itemId] = true;
      }
      return newState;
    });
  };

  // Load user settings
  useEffect(() => {
    const loadUserSettings = async () => {
      // No need for direct auth call here
      if (!user) return;
      
      try {
        // If we already have cached settings, use them initially
        const cachedSettings = await getData<{ unified_home_settings: UnifiedHomeSettings }>(HOME_CACHE_KEYS.USER_SETTINGS);
        
        // Always fetch from database to ensure we have the latest settings
        const { data: userSettings, error } = await supabase
          .from('user_settings')
          .select('unified_home_settings')
          .eq('user_id', user.id)
          .single();
        
        if (error) throw error;

        if (userSettings?.unified_home_settings) {
          const settings = userSettings.unified_home_settings as UnifiedHomeSettings;
          setViewMode(settings.viewMode || defaultSettings.viewMode);
          setSortBy(settings.sortBy || defaultSettings.sortBy);
          setGroupByType(settings.groupByType ?? defaultSettings.groupByType);
          setItemsPerPage(settings.itemsPerPage || defaultSettings.itemsPerPage);
          setScrollMode(settings.scrollMode || defaultSettings.scrollMode);
          
          // Cache the settings with the correct structure
          await setData(HOME_CACHE_KEYS.USER_SETTINGS, userSettings);
        } else if (!cachedSettings?.data) {
          // If no settings in DB and no cache, use defaults and save them
          await saveUserSettings(defaultSettings);
        }
      } catch (error) {
        logger.error('Error loading user settings:', error);
      }
    };

    loadUserSettings();
  }, [getData, setData, user]);

  // Save user settings when view mode changes
  const saveUserSettings = async (settings: Partial<UnifiedHomeSettings>) => {
    // No need for direct auth call
    if (!user) return;
    
    try {
      const { data: existingData } = await supabase
        .from('user_settings')
        .select('unified_home_settings')
        .eq('user_id', user.id)
        .single();
      
      const currentSettings = existingData?.unified_home_settings || defaultSettings;
      const newSettings = { ...currentSettings, ...settings };
      
      logger.debug('Saving user settings:', newSettings);
      
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          unified_home_settings: newSettings
        });
      
      if (error) throw error;
      
      // Update cache
      await setData(HOME_CACHE_KEYS.USER_SETTINGS, { unified_home_settings: newSettings });
    } catch (error) {
      logger.error('Error saving user settings:', error);
    }
  };

  // Update handleViewModeChange
  const handleViewModeChange = async (mode: ViewMode) => {
    setViewMode(mode);
    await saveUserSettings({ viewMode: mode });
  };

  // Update handleSortChange
  const handleSortChange = async (option: SortOption) => {
    setSortBy(option);
    await saveUserSettings({ sortBy: option });
  };

  // Add handleGroupByChange
  const handleGroupByChange = async (enabled: boolean) => {
    setGroupByType(enabled);
    await saveUserSettings({ groupByType: enabled });
  };

  // Add handler for scroll mode changes
  const handleScrollModeChange = async (mode: 'pagination' | 'infinite') => {
    setScrollMode(mode);
    await saveUserSettings({ scrollMode: mode });
  };

  // Add handler for type visibility changes
  const handleTypeVisibilityChange = (type: keyof TypeVisibility, value: boolean) => {
    setTypeVisibility(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Transform functions for notes and cards
  const transformToFilterItem = (item: Note | CareerCard): FilterItem => {
    // Check if it's a CareerCard by looking for cardid or card_data
    if ('cardid' in item && item.cardid) {
      const card = item as CareerCard;
      return {
        id: card.cardid,
        title: card.card_data?.title || card.title || 'Untitled Card',
        type: 'card',
        custom_tags: card.card_data?.custom_tags || [],
        date_tags: card.card_data?.date_tags || []
      };
    } else { // It's a Note
      const note = item as Note;
      return {
        id: note.id,
        title: note.title,
        type: 'note',
        custom_tags: note.custom_tags || [],
        date_tags: note.date_tags || []
      };
    }
  };

  // Update the pin toggle handler
  const handlePinToggle = async (item: CombinedItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent item click from triggering

    try {
      const newPinnedState = !item.is_pinned;

      // Update local state immediately
      if (item.type === 'review') {
        setReviews(prev => prev.map(review => 
          review.id === item.id 
            ? { ...review, is_pinned: newPinnedState }
            : review
        ));
      } else if (item.type === 'note') {
        setNotes(prev => prev.map(note => 
          note.id === item.id 
            ? { ...note, is_pinned: newPinnedState }
            : note
        ));
      } else if (item.type === 'card') {
        setCareerCards(prev => prev.map(card => 
          card.cardid === item.id 
            ? { ...card, is_pinned: newPinnedState }
            : card
        ));
      }

      // Update database
      if (item.type === 'review') {
        await supabase
          .from('reviews')
          .update({ is_pinned: newPinnedState })
          .eq('id', item.id);
      } else if (item.type === 'note') {
        await supabase
          .from('notes')
          .update({ is_pinned: newPinnedState })
          .eq('id', item.id);
      } else if (item.type === 'card') {
        await supabase
          .from('career_cards')
          .update({ is_pinned: newPinnedState })
          .eq('cardid', item.id);
      }

      // Smart cache invalidation based on item type
      if (item.type === 'review') {
        invalidateCache('reviews');
      } else if (item.type === 'note') {
        invalidateCache('notes');
      } else if (item.type === 'card') {
        invalidateCache('career-cards');
      }
    } catch (error) {
      console.error('Error toggling pin status:', error);
    }
  };

  // Update the processedItems useMemo to use the group counts cache
  const processedItemsResult = useMemo(() => {
    // Remove the async fetchGroupCounts call from useMemo - this should be handled in useEffect
    // useMemo should be synchronous and not have side effects
    
    const allItems: CombinedItem[] = [];

    // Add reviews from local state
    allItems.push(...(reviews || [])
      .filter(review => review && review.id && review.created_at && !isNaN(new Date(review.created_at).getTime()))
      .map(review => ({
        id: review.id,
        type: 'review' as const,
        title: review.review_name,
        content: review.review_text,
        custom_tags: review.custom_tags,
        date_tags: review.date_tags,
        created_at: review.created_at,
        is_pinned: review.is_pinned || false,
        review
      })));

    // Add notes from local state
    allItems.push(...(notes || [])
      .filter(note => note && note.id && note.created_at && !isNaN(new Date(note.created_at).getTime()))
      .map(note => ({
        id: note.id,
        type: 'note' as const,
        title: note.title,
        content: note.content,
        custom_tags: note.custom_tags,
        date_tags: note.date_tags,
        created_at: note.created_at,
        is_pinned: note.is_pinned || false,
        note
      })));

    // Add career cards from local state
    allItems.push(...(careerCards || [])
      .filter((card): card is CareerCard => {
        const hasTitle = Boolean(card?.title || card?.card_data?.title);
        const hasType = Boolean(card?.type || card?.card_data?.type);
        const hasValidDate = Boolean(card?.created_at && !isNaN(new Date(card.created_at).getTime()));
        const isValid = Boolean(
          card && 
          (card.cardid || card.id) && 
          card.card_data &&
          hasTitle &&
          hasType &&
          hasValidDate
        );
        return isValid;
      })
      .map(card => ({
        id: card.cardid || card.id,
        type: 'card' as const,
        title: card.title || card.card_data.title || '',
        content: card.card_data.overview || '',
        custom_tags: card.card_data.custom_tags || [],
        date_tags: card.card_data.date_tags || [],
        created_at: card.created_at,
        is_pinned: card.is_pinned || false,
        card
      })));

    // Filter items
    const filteredItems = allItems.filter((item: CombinedItem) => {
      const visibilityType = item.type === 'review' 
        ? 'reviews' 
        : item.type === 'note' 
          ? 'notes' 
          : 'careerCards';
      
      if (!typeVisibility[visibilityType]) return false;

      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        (item.title || '').toLowerCase().includes(searchLower) ||
        (item.content || '').toLowerCase().includes(searchLower);

      const matchesTags = selectedFilterTags.length === 0 ||
        selectedFilterTags.some(tag =>
          item.custom_tags?.includes(tag) ||
          item.date_tags?.includes(tag)
        );

      const isArchived = item.custom_tags?.includes(ARCHIVE_TAG) || false;
      const matchesArchived = showArchived ? isArchived : !isArchived;

      return matchesSearch && matchesTags && matchesArchived;
    });

    // Sort items with pinned items first
    if (sortBy === 'date') {
      filteredItems.sort((a, b) => {
        if (a.is_pinned !== b.is_pinned) {
          return a.is_pinned ? -1 : 1;
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    } else {
      filteredItems.sort((a, b) => {
        if (a.is_pinned !== b.is_pinned) {
          return a.is_pinned ? -1 : 1;
        }
        return a.title.localeCompare(b.title);
      });
    }

    // Group by type if needed
    if (groupByType) {
      const groupedItems: GroupedItems = {
        reviews: [],
        notes: [],
        careerCards: []
      };

      // Group all items first to get accurate counts
      filteredItems.forEach((item: CombinedItem) => {
        const type = item.type === 'review' 
          ? 'reviews' 
          : item.type === 'note' 
            ? 'notes' 
            : 'careerCards';
        groupedItems[type].push(item);
      });

      // Calculate total pages based on the largest group size
      const maxGroupSize = Math.max(
        groupedItems.reviews.length,
        groupedItems.notes.length,
        groupedItems.careerCards.length
      );
      const calculatedTotalPages = Math.max(1, Math.ceil(maxGroupSize / itemsPerPage));

      // For infinite scroll, load items incrementally
      const ITEMS_PER_LOAD = 50;
      const currentLoadCount = scrollMode === 'infinite' 
        ? currentPage * ITEMS_PER_LOAD 
        : itemsPerPage;

      // Paginate each group separately
      const paginatedGroups: GroupedItems = {
        reviews: scrollMode === 'pagination'
          ? groupedItems.reviews.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
          : groupedItems.reviews.slice(0, currentLoadCount),
        notes: scrollMode === 'pagination'
          ? groupedItems.notes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
          : groupedItems.notes.slice(0, currentLoadCount),
        careerCards: scrollMode === 'pagination'
          ? groupedItems.careerCards.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
          : groupedItems.careerCards.slice(0, currentLoadCount)
      };

      return {
        items: paginatedGroups,
        totalPages: calculatedTotalPages,
        totalCounts: groupCounts || {
          reviews: groupedItems.reviews.length,
          notes: groupedItems.notes.length,
          careerCards: groupedItems.careerCards.length
        },
        hasMore: {
          reviews: groupedItems.reviews.length > currentLoadCount,
          notes: groupedItems.notes.length > currentLoadCount,
          careerCards: groupedItems.careerCards.length > currentLoadCount
        }
      };
    }

    // If not grouping, handle flat list
    const calculatedTotalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
    
    // For infinite scroll, load items incrementally
    const ITEMS_PER_LOAD = 20;
    const currentLoadCount = scrollMode === 'infinite' 
      ? currentPage * ITEMS_PER_LOAD 
      : itemsPerPage;

    return {
      items: scrollMode === 'pagination'
        ? filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        : filteredItems.slice(0, currentLoadCount),
      totalPages: calculatedTotalPages,
      metadata: {
        totalCount: filteredItems.length,
        hasMore: filteredItems.length > currentLoadCount
      }
    };
  }, [reviews, notes, careerCards, typeVisibility, searchQuery, selectedFilterTags, showArchived, sortBy, groupByType, currentPage, itemsPerPage, scrollMode, groupCounts]);

  // Add useEffect to handle async group counts fetching
  useEffect(() => {
    const fetchGroupCounts = async () => {
      // No need for direct auth call
      if (!isAuthenticated || !user || !groupByType) return;
      
      // Check cache validity (5 second cache)
      const now = Date.now();
      const cacheAge = now - groupCountsCache.current.timestamp;
      const cacheValid = cacheAge < 5000 && 
        groupCountsCache.current.query === searchQuery &&
        JSON.stringify(groupCountsCache.current.tags) === JSON.stringify(selectedFilterTags) &&
        groupCountsCache.current.archived === showArchived;

      if (cacheValid && groupCountsCache.current.data) {
        return;
      }

      try {
        // Get group counts using the get_content_type_counts function
        const { data: groupCountsData, error: countError } = await supabase
          .rpc('get_content_type_counts', {
            p_user_id: user.id,
            p_search_query: searchQuery || null,
            p_filter_tags: selectedFilterTags.length > 0 ? selectedFilterTags : null,
            p_show_archived: showArchived
          });

        if (countError) {
          console.error('Error fetching group counts:', countError);
          return;
        }

        // Transform counts into our expected format
        const counts: GroupCounts = {
          reviews: 0,
          notes: 0,
          careerCards: 0
        };

        if (groupCountsData) {
          groupCountsData.forEach(({ item_type, count }: { item_type: string; count: number }) => {
            switch (item_type) {
              case 'review':
                counts.reviews = Number(count);
                break;
              case 'note':
                counts.notes = Number(count);
                break;
              case 'card':
                counts.careerCards = Number(count);
                break;
            }
          });
        }

        // Update cache
        groupCountsCache.current = {
          data: counts,
          timestamp: now,
          query: searchQuery,
          tags: [...selectedFilterTags],
          archived: showArchived
        };

        setGroupCounts(counts);
      } catch (error) {
        console.error('Error fetching group counts:', error);
      }
    };

    if (groupByType) {
      fetchGroupCounts();
    }
  }, [isAuthenticated, user, groupByType, searchQuery, selectedFilterTags, showArchived]);

  // Update total pages when processed items change
  useEffect(() => {
    setTotalPages(processedItemsResult.totalPages);
  }, [processedItemsResult.totalPages]);

  const processedItems = processedItemsResult.items;
  const itemMetadata = processedItemsResult.metadata;

  // Add infinite scroll handler
  useEffect(() => {
    if (scrollMode !== 'infinite') return;

    const handleScroll = () => {
      if (!listContainerRef.current || !itemMetadata) return;

      const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current;
      const scrollThreshold = 100; // pixels from bottom to trigger load more

      if (scrollHeight - (scrollTop + clientHeight) < scrollThreshold) {
        const hasMoreItems = groupByType
          ? Object.values(itemMetadata.hasMore).some(hasMore => hasMore)
          : itemMetadata.hasMore;

        if (hasMoreItems) {
          setCurrentPage(prev => prev + 1);
        }
      }
    };

    const listContainer = listContainerRef.current;
    if (listContainer) {
      listContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (listContainer) {
        listContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollMode, isLoadingMore, itemMetadata, groupByType]);

  // Render section headers with total counts
  const renderSectionHeader = (type: string, items: CombinedItem[]) => {
    const totalCount = groupByType && itemMetadata?.totalCounts
      ? (itemMetadata.totalCounts as any)[type]
      : items.length;
    
    return (
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800">
        <h2 className="text-lg font-semibold">
          {type.charAt(0).toUpperCase() + type.slice(1)} ({totalCount})
        </h2>
      </div>
    );
  };

  const location = useLocation();
  const { openNoteId, shouldOpenNote, openCardId, shouldOpenCard, openReviewId: reviewIdFromNav, shouldOpenReview } = location.state || {};

  // Effect to handle opening notes from navigation
  useEffect(() => {
    if (openNoteId && shouldOpenNote && cachedNotes) {
      const noteToOpen = cachedNotes.find(note => note.id === openNoteId);
      if (noteToOpen) {
        setSelectedNote(noteToOpen);
        setSelectedReview(null);
        setSelectedCard(null);

        // Scroll into view if requested
        if (location.state?.shouldScrollIntoView) {
          const scrollToElement = (retries = 0) => {
            const element = document.getElementById(`note-${openNoteId}`);
            if (element) {
              // First scroll the element into center view
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Then adjust the scroll position slightly upward after the smooth scroll completes
              setTimeout(() => {
                const offset = window.innerHeight * 0.1; // 10% of viewport height
                window.scrollBy({ top: -offset, behavior: 'smooth' });
              }, 500);
            } else if (retries < 5) { // Try up to 5 times
              // Retry with increasing delays (exponential backoff)
              setTimeout(() => scrollToElement(retries + 1), 500 * Math.pow(1.5, retries));
            }
          };
          
          // Start the first attempt after a longer initial delay
          setTimeout(() => scrollToElement(), 1000);
        }
      }
    }
  }, [openNoteId, shouldOpenNote, cachedNotes, location.state?.shouldScrollIntoView]);

  // Effect to handle opening career cards from navigation
  useEffect(() => {
    if (openCardId && shouldOpenCard) {
      // If data is still loading, wait for it
      if (cardsLoading) {
        return;
      }

      // Try to find the card
      const cardToOpen = cachedCards.find(card => card.cardid === openCardId);
      
      if (cardToOpen) {
        setSelectedCard(cardToOpen);
        setSelectedReview(null);
        setSelectedNote(null);

        // Clear navigation state after successful opening
        window.history.replaceState({}, document.title);

        // Scroll into view if requested
        if (location.state?.shouldScrollIntoView) {
          const scrollToElement = (retries = 0) => {
            const element = document.getElementById(`card-${openCardId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              setTimeout(() => {
                const offset = window.innerHeight * 0.1;
                window.scrollBy({ top: -offset, behavior: 'smooth' });
              }, 500);
            } else if (retries < 5) {
              setTimeout(() => scrollToElement(retries + 1), 500 * Math.pow(1.5, retries));
            }
          };
          
          setTimeout(() => scrollToElement(), 1000);
        }
      } else if (!cardsLoading && cachedCards.length > 0) {
        // Data has loaded but card not found
        console.warn(`Career card with ID ${openCardId} not found`);
        // Clear navigation state even if card not found
        window.history.replaceState({}, document.title);
      }
    }
  }, [openCardId, shouldOpenCard, cachedCards, cardsLoading, location.state?.shouldScrollIntoView]);

  // Effect to handle opening reviews from navigation
  useEffect(() => {
    if (reviewIdFromNav && shouldOpenReview && cachedReviews) {
      const reviewToOpen = cachedReviews.find(review => review.id === reviewIdFromNav);
      if (reviewToOpen) {
        setSelectedReview(reviewToOpen);
        setSelectedNote(null);
        setSelectedCard(null);

        // Scroll into view if requested
        if (location.state?.shouldScrollIntoView) {
          const scrollToElement = (retries = 0) => {
            const element = document.getElementById(`review-${reviewIdFromNav}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              setTimeout(() => {
                const offset = window.innerHeight * 0.1;
                window.scrollBy({ top: -offset, behavior: 'smooth' });
              }, 500);
            } else if (retries < 5) {
              setTimeout(() => scrollToElement(retries + 1), 500 * Math.pow(1.5, retries));
            }
          };
          
          setTimeout(() => scrollToElement(), 1000);
        }
      }
    }
  }, [reviewIdFromNav, shouldOpenReview, cachedReviews, location.state?.shouldScrollIntoView]);

  // Effect to handle creating a new card from navigation (e.g., from InternshipDashboard)
  useEffect(() => {
    // Check both location state and sessionStorage
    let cardData = location.state;
    
    // If no location state, check sessionStorage as backup
    if (!cardData?.createCard) {
      const storedData = sessionStorage.getItem('pendingCardCreation');
      if (storedData) {
        try {
          cardData = JSON.parse(storedData);
          sessionStorage.removeItem('pendingCardCreation');
        } catch (e) {
          console.error('Failed to parse stored card data:', e);
        }
      }
    }
    
    
    if (cardData?.createCard && cardData?.cardType) {
      
      // Directly create and select the card
      const type = cardData.cardType;
      const initialData = cardData.initialData;
      const now = new Date();
      const newCard: CareerCardType = {
        cardid: '',
        id: '',
        card_version: 1,
        created_at: now.toISOString(),
        career_card_date: now.toISOString(),
        is_pinned: false,
        title: (initialData?.title as string) || '',
        type,
        career_impact: { value: 3, last_updated: now.toISOString() },
        engagement_level: { value: 3, last_updated: now.toISOString() },
        card_data: {
          type,
          title: (initialData?.title as string) || '',
          custom_tags: ['Active'],
          date_tags: [],
          overview: (initialData?.overview as string) || '',
          ...initialData  // Include any additional initial data
        }
      };
      setSelectedCard(newCard);
      setSelectedNote(null);
      setSelectedReview(null);
      setIsCardTypeModalOpen(false);
      
      // Clear navigation state after using it
      if (location.state?.createCard) {
        window.history.replaceState({}, document.title);
      }
    } else if (cardData?.createCard) {
      // Open the CardTypeModal if no type specified
      setIsCardTypeModalOpen(true);
    }
  }, [location]);

  // Update allTags useMemo to include career cards - optimize for performance
  const allTags = useMemo(() => {
    // Early return if no data to avoid unnecessary processing
    if (!cachedReviews?.length && !cachedNotes?.length && !cachedCards?.length) {
      return [];
    }

    const reviewItems: FilterItem[] = cachedReviews?.map(review => ({
      id: review.id,
      title: review.review_name,
      type: 'review',
      custom_tags: review.custom_tags,
      date_tags: review.date_tags
    })) || [];

    const noteItems: FilterItem[] = cachedNotes?.map(note => ({
      id: note.id,
      title: note.title,
      type: 'note',
      custom_tags: note.custom_tags,
      date_tags: note.date_tags
    })) || [];

    const cardItems: FilterItem[] = cachedCards?.map(card => ({
      id: card.cardid || card.id,
      title: card.title,  // Use root-level title
      type: 'card',
      custom_tags: card.custom_tags || card.card_data?.custom_tags || [],
      date_tags: card.date_tags || card.card_data?.date_tags || []
    })) || [];

    // Only call extractTags if we have items to process
    const allItems = [...reviewItems, ...noteItems, ...cardItems];
    if (allItems.length === 0) {
      return [];
    }

    return extractTags(allItems, {
      reviews: {
        customTags: true,
        dateTags: true
      },
      notes: {
        customTags: true,
        dateTags: true
      },
      cards: {
        customTags: true,
        dateTags: true
      }
    });
  }, [cachedReviews?.length, cachedNotes?.length, cachedCards?.length, cachedReviews, cachedNotes, cachedCards]); // Add length checks to reduce unnecessary recalculations

  useEffect(() => {
    const fetchAllTags = async () => {
      // No need for direct auth call
      if (!isAuthenticated || !user) return;
      
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('custom_tags, date_tags')
          .eq('user_id', user.id);

        if (error) throw error;

        const tags = new Set<string>();
        data.forEach(review => {
          if (Array.isArray(review.custom_tags)) {
            review.custom_tags.forEach(tag => tags.add(tag));
          }
        });
        setAllUserTags(Array.from(tags));
      } catch (error) {
        console.error('Error fetching all tags:', error);
      }
    };

    fetchAllTags();
  }, [isAuthenticated, user]);

  const handleAddTag = () => {
    if (!newTag.trim() || !selectedReview) return;

    const tag = newTag.trim();
    if (!editedCustomTags.includes(tag)) {
      setEditedCustomTags([...editedCustomTags, tag]);
    }
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditedCustomTags(editedCustomTags.filter(tag => tag !== tagToRemove));
  };

  const handleAddSuggestedTag = (tag: string) => {
    if (!editedCustomTags.includes(tag)) {
      setEditedCustomTags([...editedCustomTags, tag]);
      setSuggestedTags(prev => prev.filter(t => t !== tag));
    }
  };

  const fetchSuggestedTags = async () => {
    if (!selectedReview?.review_text) {
      alert("Please enter some content before generating tags.");
      return;
    }

    setIsLoadingTags(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { data, error } = await supabase.functions.invoke('suggest-tags', {
        body: {
          content: selectedReview.review_text,
          existingTags: editedCustomTags,
          allUserTags
        }
      });

      if (error) throw error;

      if (data && Array.isArray(data.suggestedTags)) {
        setSuggestedTags(data.suggestedTags);
      }
    } catch (error) {
      console.error('Error generating tags:', error);
      alert("Failed to generate tags. Please try again.");
    } finally {
      setIsLoadingTags(false);
    }
  };

  // Add this function to handle markdown preview
  const handlePreviewChange = (value?: string) => {
    if (value !== undefined) {
      setEditedReviewText(value);
    }
  };

  // Update the renderNotes function to include pagination controls
  const renderNotes = () => {
    // Show loading state only if we have no cached data at all
    if ((reviewsLoading || cardsLoading || notesLoading) && 
        !cachedReviews?.length && !cachedNotes?.length && !cachedCards?.length) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Add pagination controls at the top */}
        {scrollMode === 'pagination' && (
          <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  const newItemsPerPage = parseInt(e.target.value);
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1); // Reset to first page when changing items per page
                  saveUserSettings({ itemsPerPage: newItemsPerPage });
                }}
                className="form-select rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                aria-label="Items per page"
              >
                <option value="10">10 / page</option>
                <option value="20">20 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, Math.min(currentPage - 1, totalPages)))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
        
        <div 
          ref={listContainerRef}
          className="flex-1 overflow-y-auto"
        >
          {/* Existing content rendering */}
          {Array.isArray(processedItems) ? (
            processedItems.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-gray-400">No items found</p>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                {processedItems.map((item: CombinedItem) => renderItem(item))}
              </AnimatePresence>
            )
          ) : (
            Object.entries(processedItems)
              .sort(([typeA], [typeB]) => {
                const order = { notes: 0, careerCards: 1, reviews: 2 };
                return order[typeA as keyof typeof order] - order[typeB as keyof typeof order];
              })
              .map(([type, groupItems]) => (
                <div key={type} className="mb-4">
                  {/* Existing group rendering logic */}
                  <div className="relative group">
                    <button
                      onClick={() => toggleTypeCollapse(type)}
                      className="w-full flex items-center px-2 py-2 focus:outline-none"
                    >
                      <div className="flex items-center">
                        <span className="font-medium capitalize text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {type === 'careerCards' ? 'Career Cards' : type === 'reviews' ? 'Reviews' : 'Notes'}
                        </span>
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({groupItems.length})</span>
                      </div>
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200 dark:bg-gray-700 group-hover:bg-blue-600 dark:group-hover:bg-blue-400 transition-colors" />
                  </div>
                  <AnimatePresence>
                    {!collapsedTypes[type] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-1"
                      >
                        {groupItems.map((item: CombinedItem) => renderItem(item))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))
          )}
        </div>

        {/* Keep existing bottom pagination controls */}
        {scrollMode === 'pagination' && (
          <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  const newItemsPerPage = parseInt(e.target.value);
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1); // Reset to first page when changing items per page
                  saveUserSettings({ itemsPerPage: newItemsPerPage });
                }}
                className="form-select rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                aria-label="Items per page"
              >
                <option value="10">10 / page</option>
                <option value="20">20 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, Math.min(currentPage - 1, totalPages)))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Keep existing infinite scroll loading indicator */}
        {scrollMode === 'infinite' && isLoadingMore && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>
    );
  };

  // Update the item rendering in the list panel
  const renderItem = (item: CombinedItem) => (
    <motion.div
      key={`${item.type}-${item.id}`}
      id={`${item.type}-${item.id}`}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className={`relative flex ${viewMode === 'condensed'
        ? 'px-0.5 py-1 mb-1'
        : 'px-0.5 py-2 mb-2'
      } rounded-lg cursor-pointer transition-colors ${
        selectedItems[item.id]
          ? 'bg-blue-50 dark:bg-blue-900/30'
          : item.type === 'review' && selectedReview?.id === item.id
            ? 'bg-purple-100 dark:bg-purple-900'
            : item.type === 'note' && selectedNote?.id === item.id
              ? 'bg-green-100 dark:bg-green-900'
              : item.type === 'card' && selectedCard?.cardid === item.id
                ? 'bg-blue-100 dark:bg-blue-900'
                : 'bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800'
      }`}
      tabIndex={0}  // Make the entire item focusable
      onClick={() => handleItemClick(item)}
      onKeyDown={(e) => {
        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault();
            handleItemClick(item);
            break;
          case 'ArrowDown':
            e.preventDefault();
            focusNextItem(`${item.type}-${item.id}`, 'down');
            break;
          case 'ArrowUp':
            e.preventDefault();
            focusNextItem(`${item.type}-${item.id}`, 'up');
            break;
        }
      }}
      role="button"
      aria-label={`${item.title} (${item.type})`}
    >
      {/* Selection bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-3 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors ${
          selectedItems[item.id] ? 'bg-blue-500 dark:bg-blue-600' : ''
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleItemSelection(e, item.id);
        }}
      />
      
      {/* Existing content with adjusted padding */}
      <div className="flex-1 pl-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {/* Existing type indicator and title */}
            <div className={`float-right ml-2 ${viewMode === 'condensed'
              ? 'px-1.5 py-0.5 text-xs'
              : 'px-2 py-1 text-xs'
            } rounded-full ${item.type === 'review'
              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
              : item.type === 'note'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            }`}>
              {item.type === 'review' ? 'Review' : item.type === 'note' ? 'Note' : 'Card'}
            </div>

            <h3 className={`font-semibold ${viewMode === 'condensed' 
              ? 'text-sm line-clamp-1' 
              : 'text-base'
            }`}>{item.title}</h3>
            <p className={`${viewMode === 'condensed' ? 'text-xs' : 'text-sm'
              } text-gray-600 dark:text-gray-400`}>
              {item.created_at && !isNaN(new Date(item.created_at).getTime()) 
                ? formatWithTimezone(new Date(item.created_at))
                : 'Date unavailable'}
            </p>
            <p className={`${viewMode === 'condensed' ? 'text-xs mt-0.5 line-clamp-1' : 'text-sm mt-1 line-clamp-2'
              }`}>
              {item.type === 'review'
                ? cleanMarkdown(item.content)
                : item.type === 'card' && item.card?.card_data.overview
                  ? item.card.card_data.overview
                  : item.content}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePinToggle(item, e);
            }}
            className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ${
              item.is_pinned ? 'text-yellow-500' : 'text-gray-400 dark:text-gray-500'
            }`}
            title={item.is_pinned ? "Unpin" : "Pin"}
            tabIndex={-1}  // Remove from tab order
            aria-label={item.is_pinned ? "Unpin item" : "Pin item"}
          >
            {item.is_pinned ? (
              <IoStarSharp size={16} />
            ) : (
              <IoStarOutline size={16} />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );

  // Add type for the group renderer
  const renderGroup = (type: string, items: CombinedItem[]) => (
    <div key={type}>
      <div
        className="flex items-center p-2 bg-gray-50 dark:bg-gray-800 cursor-pointer"
        onClick={() => setCollapsedTypes(prev => ({ ...prev, [type]: !prev[type] }))}
      >
        {collapsedTypes[type] ? <IoChevronForwardOutline /> : <IoChevronDownOutline />}
        <span className="ml-2 font-medium">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({items.length})</span>
      </div>
      {!collapsedTypes[type] && (
        <AnimatePresence mode="popLayout">
          {items.map(renderItem)}
        </AnimatePresence>
      )}
    </div>
  );

  // Add XP award types for all item types
  interface XPAwardPending {
    type: 'review' | 'note' | 'career_card';  // Use literal types instead of keyof
    action: 'create' | 'update';
    noteId?: string;
    cardId?: string;
    reviewId?: string;
  }

  // Add XP state
  const [pendingXPAward, setPendingXPAward] = useState<XPAwardPending | null>(null);

  // Add XP award effect
  useEffect(() => {
    if (!pendingXPAward) return;

    const awardXPAsync = async () => {
      try {
        logger.debug('Awarding XP', pendingXPAward);
        
        // Match the exact structure expected by awardXP
        const details = {
          action: pendingXPAward.action,
          noteId: pendingXPAward.noteId,
          cardId: pendingXPAward.cardId,
          reviewId: pendingXPAward.reviewId
        };

        await awardXP(pendingXPAward.type, details);
        logger.debug('XP awarded successfully');
      } catch (error) {
        logger.error('Error awarding XP:', error);
      } finally {
        setPendingXPAward(null);
      }
    };

    awardXPAsync();
  }, [pendingXPAward]);

  // Update handleReviewSubmit to award XP
  const handleReviewSubmit = async (reviewData: {
    reviewGoal: string;
    outputStyle: string;
    wordCount: number;
    selectedCards: FilterItem[];
    selectedNotes: FilterItem[];
  }) => {
    if (!user) {
      toast.showToast('You need to be logged in to create a review', 'error');
      return;
    }
    
    try {
      // Remove direct auth call, user is already available from useAuthState
      
      logger.debug('Creating review with data:', {
        goal: reviewData.reviewGoal,
        style: reviewData.outputStyle,
        wordCount: reviewData.wordCount,
        selectedCards: reviewData.selectedCards.length,
        selectedNotes: reviewData.selectedNotes.length,
      });
      
      // Track review creation
      trackUserJourney('review_started', {
        step_type: 'intermediate'
      });
      
      setIsCreatingReview(true);
      
      const newReview = {
        reviewGoal: reviewData.reviewGoal,
        outputStyle: reviewData.outputStyle,
        wordCount: reviewData.wordCount,
        careerCards: (cachedCards || []).filter(card =>
          reviewData.selectedCards.some(selected => selected.id === card.cardid)
        ),
        notes: (cachedNotes || []).filter(note =>
          reviewData.selectedNotes.some(selected => selected.id === note.id)
        ),
      };

      logger.debug('Creating new review with data:', newReview);

      const { data, error } = await supabase.functions.invoke('generate-review', {
        body: JSON.stringify(newReview),
      });

      if (error) throw error;

      if (!data || !data.review) {
        throw new Error('No review data received from the server');
      }

      const timestamp = Temporal.Now.zonedDateTimeISO().toString().replace(/\[.*\]/, '');
      const reviewToSave: Omit<Review, 'id' | 'type'> = {
        user_id: user.id,
        review_text: data.review,
        context_counts: {
          CareerCard: { count: reviewData.selectedCards.length },
          Note: { count: reviewData.selectedNotes.length }
        },
        career_card_ids: reviewData.selectedCards.map(card => card.id),
        note_ids: reviewData.selectedNotes.map(note => note.id),
        word_count_target: reviewData.wordCount,
        review_goal: reviewData.reviewGoal,
        output_style: reviewData.outputStyle,
        review_name: `${formatOutputStyle(reviewData.outputStyle)}, ${Temporal.Now.zonedDateTimeISO().toLocaleString()}, WCT: ${reviewData.wordCount}`,
        custom_tags: [],
        date_tags: getDateTags(timestamp, null, 'note'),
        created_at: timestamp,
        review_date: Temporal.Now.zonedDateTimeISO().toPlainDate().toString().replace(/\[.*\]/, '')
      };

      logger.debug('Saving review to database:', reviewToSave);

      const { data: savedReview, error: saveError } = await supabase
        .from('reviews')
        .insert([reviewToSave])
        .select()
        .single();

      if (saveError) {
        throw new Error(`Failed to save review: ${saveError.message}`);
      }

      if (!savedReview) {
        throw new Error('No review data received after saving');
      }

      logger.debug('Review saved successfully');

      // Invalidate and refresh reviews cache
      await invalidateCache('reviews');
      await refreshUnifiedData();

      // Select the newly created review and close the modal
      setIsNewReviewModalOpen(false);
      setSelectedReview(savedReview);
      setIsEditMode(false);

      // Publish notification event for review completion
      try {
        await NotificationEvents.reviewCompleted(user.id, {
          review_id: savedReview.id,
          review_name: savedReview.review_name,
          word_count: reviewData.wordCount,
          cards_count: reviewData.selectedCards.length,
          notes_count: reviewData.selectedNotes.length
        });
      } catch (notificationError) {
        console.warn('Failed to publish review completion notification:', notificationError);
      }

      // Queue XP award for creating a new review
      setPendingXPAward({
        type: 'review',
        action: 'create',
        reviewId: savedReview.id
      });

    } catch (error) {
      logger.error('Error creating review:', error);
      throw error;
    } finally {
      setIsCreatingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      // Update local state
      setSelectedReview(null);

      // Refresh the unified data
      await invalidateCache('reviews');
      await refreshUnifiedData();
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (selectedReview) {
      setEditedReviewText(selectedReview.review_text);
      setEditedCustomTags(selectedReview.custom_tags || []);
      setEditedDateTags(selectedReview.date_tags || []);
    }
  }, [selectedReview]);

  const handleEditClick = () => {
    if (!selectedReview) return;

    // Set initial state for editing
    setEditedReviewText(selectedReview.review_text);
    setEditedCustomTags(selectedReview.custom_tags || []);
    setSuggestedTags([]);
    setNewTag('');
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    // Reset all edit state
    setEditedReviewText(selectedReview?.review_text || '');
    setEditedCustomTags(selectedReview?.custom_tags || []);
    setSuggestedTags([]);
    setNewTag('');
    setIsEditMode(false);
  };

  // Update handleReviewSubmit to award XP
  const handleUpdateReview = async () => {
    if (!selectedReview || !editedReviewText.trim()) {
      alert('Review text cannot be empty');
      return;
    }

    try {
      logger.debug('Starting review update:', {
        id: selectedReview.id,
        textLength: editedReviewText.length,
        editedTags: editedCustomTags,
        reviewName: selectedReview.review_name
      });

      // Generate date tags using review_date
      const dateTags = getDateTags(selectedReview.review_date, selectedReview.review_date, 'review');
      logger.debug('Generated date tags:', dateTags);

      const updateData = {
        review_text: editedReviewText.trim(),
        custom_tags: editedCustomTags || [],
        date_tags: dateTags || [],
        // Preserve other required fields
        context_counts: selectedReview.context_counts,
        career_card_ids: selectedReview.career_card_ids || [],
        note_ids: selectedReview.note_ids || [],
        review_name: selectedReview.review_name,
        review_goal: selectedReview.review_goal,
        output_style: selectedReview.output_style,
        word_count_target: selectedReview.word_count_target
      };

      logger.debug('Sending update to supabase:', updateData);

      const { data, error } = await supabase
        .from('reviews')
        .update(updateData)
        .eq('id', selectedReview.id)
        .select('*')
        .single();

      if (error) {
        logger.error('Supabase error updating review:', error);
        throw error;
      }

      if (!data) {
        logger.error('No data returned from update');
        throw new Error('No data returned from update');
      }

      logger.debug('Review updated successfully:', {
        id: data.id,
        textLength: data.review_text.length,
        customTags: data.custom_tags,
        dateTags: data.date_tags
      });

      // Update the selected review in state
      setSelectedReview(data);

      // Invalidate and refresh unified cache
      await invalidateCache('user-unified-content');
      await refreshUnifiedData();

      // Clean up edit state
      setSuggestedTags([]);
      setNewTag('');
      setIsEditMode(false);

      // Queue XP award for updating review
      setPendingXPAward({
        type: 'review',
        action: 'update',
        reviewId: selectedReview.id
      });

    } catch (error: any) {
      logger.error('Error updating review:', {
        error: error.message,
        details: error.details || 'No additional details',
        hint: error.hint || 'No hint provided'
      });
      alert(`Failed to update review: ${error.message || 'Unknown error'}`);
    }
  };

  const handleArchiveToggle = async () => {
    if (!selectedReview) return;

    try {
      logger.debug('Toggling archive status for review:', selectedReview.id);

      const currentTags = selectedReview.custom_tags || [];
      const isArchived = currentTags.includes(ARCHIVE_TAG);
      const newTags = isArchived
        ? currentTags.filter(tag => tag !== ARCHIVE_TAG)
        : [...currentTags, ARCHIVE_TAG];

      // Update local state immediately
      const updatedReview = {
        ...selectedReview,
        custom_tags: newTags
      };
      setSelectedReview(updatedReview);

      // Perform the actual update in the background
      const { error } = await supabase
        .from('reviews')
        .update({
          custom_tags: newTags
        })
        .eq('id', selectedReview.id);

      if (error) throw error;

      // Invalidate cache and refresh in background
      await invalidateCache('user-unified-content');
      refreshUnifiedData().catch(refreshError =>
        logger.error('Error refreshing unified data:', refreshError)
      );

      logger.debug(`Review ${isArchived ? 'unarchived' : 'archived'} successfully`);
    } catch (error: any) {
      logger.error('Error toggling archive status:', error);
      alert(`Failed to ${selectedReview.custom_tags?.includes(ARCHIVE_TAG) ? 'unarchive' : 'archive'} review: ${error.message}`);

      // Revert local state on error
      refreshReviews().catch(refreshError =>
        logger.error('Error refreshing reviews after error:', refreshError)
      );
    }
  };

  const handleNoteClick = (note: Note) => {
    setSelectedNote(note);
    setSelectedReview(null);
  };

  const handleNoteClose = () => {
    setSelectedNote(null);
  };

  // Update handleNoteSave to award XP
  const handleNoteSave = async (updatedNote: Note): Promise<Note> => {
    const startTime = performance.now();
    
    // Define isNewNote here
    const isNewNote = !updatedNote.id;
    
    if (!user) {
      throw new Error('You must be logged in to save notes');
    }
    
    // Reset tracking flag for new save operation
    // hasTrackedNoteSaveRef.current = false;
    
    try {
      let data;
      
      // Get current user from useAuthState instead of direct call
      if (!user) throw new Error('No authenticated user');

      // Ensure note_date is in ISO format
      const noteDate = new Date(updatedNote.note_date);
      const formattedNoteDate = noteDate.toISOString();
      
      // Track journey step
      if (isNewNote) {
        trackUserJourney('note_data_entered', {
          step_type: 'intermediate'
        });
      }
      
      if (!updatedNote.id) {
        // This is a new note - insert
        const { data: insertedData, error: insertError } = await supabase
          .from('notes')
          .insert({
            title: updatedNote.title,
            content: updatedNote.content,
            custom_tags: updatedNote.custom_tags,
            date_tags: updatedNote.date_tags,
            note_date: formattedNoteDate,
            career_impact: updatedNote.career_impact,
            engagement_level: updatedNote.engagement_level,
            user_id: user.id,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (insertError) throw insertError;
        data = insertedData;
      } else {
        // This is an existing note - update
        const { data: updatedData, error: updateError } = await supabase
          .from('notes')
          .update({
            title: updatedNote.title,
            content: updatedNote.content,
            custom_tags: updatedNote.custom_tags,
            date_tags: updatedNote.date_tags,
            note_date: formattedNoteDate,
            career_impact: updatedNote.career_impact,
            engagement_level: updatedNote.engagement_level
          })
          .eq('id', updatedNote.id)
          .select()
          .single();

        if (updateError) throw updateError;
        data = updatedData;
      }

      if (!data) throw new Error('No data returned from operation');

      // Update local state
      setSelectedNote(data);

      // Refresh the unified data
      invalidateCache('notes');
      await refreshUnifiedData();

      // Publish notification event for new notes
      if (isNewNote) {
        try {
          await NotificationEvents.noteCreated(user.id, {
            note_id: data.id,
            title: data.title || 'Untitled Note',
            content: data.content,
            tags: [...(data.custom_tags || []), ...(data.date_tags || [])]
          });
        } catch (notificationError) {
          console.warn('Failed to publish note creation notification:', notificationError);
        }
      }

      // Queue XP award
      setPendingXPAward({
        type: 'note',
        action: updatedNote.id ? 'update' : 'create',
        noteId: data.id
      });
      
      // Track successful save
      const duration = performance.now() - startTime;
      
      // Track feature usage
      trackFeatureUsage(isNewNote ? 'create_note' : 'update_note', 'complete', {
        duration_ms: Math.round(duration)
      });
      
      // Complete journey if creating new note
      if (isNewNote) {
        trackUserJourney('note_saved', {
          step_type: 'success'
        });
      }

      return data;
    } catch (error) {
      console.error('Error saving note:', error);
      
      // Track failed save
      const duration = performance.now() - startTime;
      
      // Track feature usage failure
      trackFeatureUsage(isNewNote ? 'create_note' : 'update_note', 'error', {
        duration_ms: Math.round(duration),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      // Record error in journey if creating new note
      if (isNewNote) {
        trackUserJourney('note_save_error', {
          step_type: 'error',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
      
      throw error;
    }
  };

  const handleNoteDelete = async (noteId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      // Update local state
      setSelectedNote(null);

      // Refresh the unified data
      invalidateCache('notes');
      await refreshUnifiedData();
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  };

  // Add toggle function
  const toggleTypeCollapse = (type: string) => {
    setCollapsedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Add useEffect for click outside handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add useEffect for click outside handling of create dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target as Node)) {
        setIsCreateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add career card handlers
  const handleCardSelect = (card: CareerCard) => {
    setSelectedCard(card);
    setSelectedNote(null);
    setSelectedReview(null);
  };

  // Update handleCardSave to award XP
  const handleCardSave = async (updatedCard: CareerCard): Promise<CareerCard> => {
    const startTime = performance.now();
    const isNewCard = !updatedCard.cardid && !updatedCard.id;
    
    try {
      logger.debug('Saving card:', { cardId: updatedCard.cardid });
      
      if (!user) {
        throw new Error('You must be logged in to save cards');
      }
      
      // Track journey step
      if (isNewCard) {
        trackUserJourney('card_data_entered', {
          step_type: 'intermediate'
        });
      }

      // Remove duplicated user check and direct auth call
      if (!user) throw new Error('No user found');

      let result;
      if (!updatedCard.cardid && !updatedCard.id) {
        // Create new card - omit cardid to let Supabase generate it
        const { data, error } = await supabase
          .from('career_cards')
          .insert([{
            user_id: user.id,
            card_version: updatedCard.card_version,
            card_data: updatedCard.card_data,
            created_at: updatedCard.created_at,
            career_impact: updatedCard.career_impact,
            engagement_level: updatedCard.engagement_level
          }])
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Update existing card
        const { data, error } = await supabase
          .from('career_cards')
          .update({
            user_id: user.id,
            card_version: updatedCard.card_version,
            card_data: updatedCard.card_data,
            created_at: updatedCard.created_at,
            career_impact: updatedCard.career_impact,
            engagement_level: updatedCard.engagement_level
          })
          .eq('cardid', updatedCard.cardid)
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      if (!result) throw new Error('No data returned from save operation');

      // Update local state
      setSelectedCard(result);

      // Only refresh the unified data, don't invalidate cache
      await invalidateCache('career-cards');
      await refreshUnifiedData();

      // Publish notification event for new cards
      if (isNewCard) {
        try {
          await NotificationEvents.cardCreated(user.id, {
            card_id: result.cardid,
            title: result.card_data?.title || result.title || 'Untitled Card',
            type: result.card_data?.type || result.type || 'Unknown',
            tags: [...(result.card_data?.custom_tags || []), ...(result.card_data?.date_tags || [])]
          });
        } catch (notificationError) {
          console.warn('Failed to publish card creation notification:', notificationError);
        }
      }

      // Queue XP award
      setPendingXPAward({
        type: 'career_card',
        action: updatedCard.cardid ? 'update' : 'create',
        cardId: result.cardid
      });
      
      // Track successful save
      const duration = performance.now() - startTime;
      
      // Track feature usage
      trackFeatureUsage(isNewCard ? 'create_card' : 'update_card', 'complete', {
        duration_ms: Math.round(duration)
      });
      
      // Complete journey if creating new card
      if (isNewCard) {
        trackUserJourney('card_saved', {
          step_type: 'success'
        });
      }

      return result;
    } catch (error) {
      logger.error('Error saving card:', error);
      
      // Track failed save
      const duration = performance.now() - startTime;
      
      // Track feature usage failure
      trackFeatureUsage(isNewCard ? 'create_card' : 'update_card', 'error', {
        duration_ms: Math.round(duration),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      // Record error in journey if creating new card
      if (isNewCard) {
        trackUserJourney('card_save_error', {
          step_type: 'error',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
      
      throw error;
    }
  };

  const handleCardDelete = async (cardId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('career_cards')
        .delete()
        .eq('cardid', cardId);

      if (error) throw error;

      // Update local state
      setSelectedCard(null);

      // Only refresh the unified data, don't invalidate cache
      await invalidateCache('career-cards');
      await refreshUnifiedData();
    } catch (error) {
      logger.error('Error deleting card:', error);
      throw error;
    }
  };

  const handleItemClick = (item: CombinedItem) => {
    // If there are any selected items, we're in bulk selection mode
    if (Object.keys(selectedItems).length > 0) {
      handleItemSelection({ stopPropagation: () => {} } as React.MouseEvent, item.id);
      return;
    }

    // Check for unsaved changes
    if (hasAnyUnsavedChanges()) {
      setTargetItem(item);
      setShowUnsavedDialog(true);
      return; // Return early to prevent item switch
    }

    // Only proceed with item switch if there are no unsaved changes
    switchToItem(item);
  };

  const switchToItem = (item: CombinedItem) => {
    // Clear all edit states before switching
    clearAllEditStates();
    setNoteEditState(null);
    setReviewEditState(null);
    setCardEditState(null);

    // Switch to new item in view mode
    if (item.type === 'note') {
      setSelectedNote(item.note || null);
      setSelectedReview(null);
      setSelectedCard(null);
    } else if (item.type === 'review') {
      setSelectedNote(null);
      setSelectedReview(item.review || null);
      setSelectedCard(null);
    } else if (item.type === 'card') {
      setSelectedNote(null);
      setSelectedReview(null);
      setSelectedCard(item.card || null);
    }
  };

  // Add type for handleReviewSave
  const handleReviewSave = async (updatedReview: Review): Promise<Review> => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({
          review_name: updatedReview.review_name,
          review_text: updatedReview.review_text,
          custom_tags: updatedReview.custom_tags,
          date_tags: updatedReview.date_tags,
          review_goal: updatedReview.review_goal,
          output_style: updatedReview.output_style,
          word_count_target: updatedReview.word_count_target,
          review_date: updatedReview.review_date,
          user_id: updatedReview.user_id,
          context_counts: updatedReview.context_counts,
          career_card_ids: updatedReview.career_card_ids,
          note_ids: updatedReview.note_ids,
        })
        .eq('id', updatedReview.id)
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('No data returned from update');

      // Update local state
      setSelectedReview(data);

      // Refresh the unified data
      await invalidateCache('user-unified-content');
      await refreshUnifiedData();

      return data;
    } catch (error) {
      console.error('Error saving review:', error);
      throw error;
    }
  };

  // Add type for handleReviewDelete
  const handleReviewDelete = async (reviewId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      // Update local state
      setSelectedReview(null);

      // Refresh the reviews data
      await invalidateCache('reviews');
      await refreshUnifiedData();
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  };

  // Add this effect to track panel state changes
  useEffect(() => {
    const isAnyPanelOpen = !!selectedReview || !!selectedNote || !!selectedCard;
    if (isAnyPanelOpen) {
      setWasPanelOpen(true);
    } else {
      setWasPanelOpen(false);
    }
  }, [selectedReview, selectedNote, selectedCard]);

  // Add handleCardTypeSelect function
  const handleCardTypeSelect = useCallback((type: CardType, initialData?: Record<string, unknown>) => {
    const now = new Date();
    const newCard: CareerCardType = {
      cardid: '',
      id: '',
      card_version: 1,
      created_at: now.toISOString(),
      career_card_date: now.toISOString(),
      is_pinned: false,
      title: (initialData?.title as string) || '',
      type,
      career_impact: { value: 3, last_updated: now.toISOString() },
      engagement_level: { value: 3, last_updated: now.toISOString() },
      card_data: {
        type,
        title: (initialData?.title as string) || '',
        custom_tags: ['Active'],
        date_tags: [],
        overview: (initialData?.overview as string) || '',
        ...initialData  // Include any additional initial data
      }
    };
    setSelectedCard(newCard);
    setIsCardTypeModalOpen(false);  // Close the modal
    
    // Ensure we're not showing other panels
    setSelectedNote(null);
    setSelectedReview(null);
  }, []);

  // Add this handler near the other handlers
  const handleDeselectAll = () => {
    setSelectedItems({});
  };

  // Add undo handler
  const handleUndoArchive = async () => {
    if (!lastArchiveAction) return;
    const { items, wasArchiving } = lastArchiveAction;

    try {
      for (const item of items) {
        const currentTags = item.custom_tags || [];
        const newTags = wasArchiving
          ? currentTags.filter(tag => tag !== ARCHIVE_TAG)
          : [...currentTags, ARCHIVE_TAG];

        if (item.type === 'review') {
          await supabase
            .from('reviews')
            .update({ custom_tags: newTags })
            .eq('id', item.id)
            .then(async () => {
              await invalidateCache('reviews');
              refreshUnifiedData();
            });
        } else if (item.type === 'note') {
          await supabase
            .from('notes')
            .update({ custom_tags: newTags })
            .eq('id', item.id)
            .then(async () => {
              await invalidateCache('notes');
              refreshUnifiedData();
            });
        } else if (item.type === 'card') {
          const cardData = item.card as CareerCard;
          const updatedCardData = {
            ...cardData.card_data,
            custom_tags: newTags
          };
          
          await supabase
            .from('career_cards')
            .update({ card_data: updatedCardData })
            .eq('cardid', item.id)
            .then(async () => {
              await invalidateCache('career-cards');
              refreshUnifiedData();
            });
        }
      }

      // Refresh data
      await invalidateCache('user-unified-content');
      await refreshUnifiedData();

      // Clear the last action after successful undo
      setLastArchiveAction(null);

      // Update toast call to use the hook
      toast.showToast(
        <div>
          <div className="font-medium">Success</div>
          <div>{`Successfully undone ${wasArchiving ? 'archive' : 'unarchive'}`}</div>
        </div>,
        'success'
      );
    } catch (error) {
      console.error('Error undoing archive action:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>{'Failed to undo action'}</div>
        </div>,
        'error'
      );
    }
  };

  // Update the bulk archive toggle handler
  const handleBulkArchiveToggle = async () => {
    const allItems = Array.isArray(processedItems) ? processedItems : Object.values(processedItems).flat();
    const selectedItemsList = allItems.filter(item => selectedItems[item.id]);
    const allArchived = areAllSelectedItemsArchived(selectedItems, processedItems);

    try {
      // Store the action for potential undo before making changes
      setLastArchiveAction({
        items: selectedItemsList,
        wasArchiving: !allArchived
      });

      // Update local state immediately
      const updateLocalState = (items: CombinedItem[]) => {
        const reviewUpdates: Review[] = [];
        const noteUpdates: Note[] = [];
        const cardUpdates: CareerCard[] = [];

        items.forEach(item => {
          const currentTags = item.custom_tags || [];
          const newTags = allArchived
            ? currentTags.filter(tag => tag !== ARCHIVE_TAG)
            : [...currentTags, ARCHIVE_TAG];

          if (item.type === 'review' && item.review) {
            reviewUpdates.push({ ...item.review, custom_tags: newTags });
          } else if (item.type === 'note' && item.note) {
            noteUpdates.push({ ...item.note, custom_tags: newTags });
          } else if (item.type === 'card' && item.card) {
            cardUpdates.push({
              ...item.card,
              card_data: {
                ...item.card.card_data,
                custom_tags: newTags
              }
            });
          }
        });

        if (reviewUpdates.length) {
          setReviews(prev => {
            const updated = [...prev];
            reviewUpdates.forEach(update => {
              const index = updated.findIndex(r => r.id === update.id);
              if (index !== -1) updated[index] = update;
            });
            return updated;
          });
        }

        if (noteUpdates.length) {
          setNotes(prev => {
            const updated = [...prev];
            noteUpdates.forEach(update => {
              const index = updated.findIndex(n => n.id === update.id);
              if (index !== -1) updated[index] = update;
            });
            return updated;
          });
        }

        if (cardUpdates.length) {
          setCareerCards(prev => {
            const updated = [...prev];
            cardUpdates.forEach(update => {
              const index = updated.findIndex(c => c.cardid === update.cardid);
              if (index !== -1) updated[index] = update;
            });
            return updated;
          });
        }
      };

      // Update local state immediately
      updateLocalState(selectedItemsList);

      // Clear selection after updating local state
      setSelectedItems({});

      // Show success toast
      const message = `Successfully ${allArchived ? 'unarchived' : 'archived'} ${selectedItemsList.length} item${selectedItemsList.length === 1 ? '' : 's'}`;
      toast.showToast(
        <div>
          <div className="font-medium">Success</div>
          <div>{message}</div>
        </div>,
        'success'
      );

      // Perform backend updates without waiting
      selectedItemsList.forEach(item => {
        const currentTags = item.custom_tags || [];
        const newTags = allArchived
          ? currentTags.filter(tag => tag !== ARCHIVE_TAG)
          : [...currentTags, ARCHIVE_TAG];

        if (item.type === 'review') {
          supabase
            .from('reviews')
            .update({ custom_tags: newTags })
            .eq('id', item.id)
            .then(async () => {
              await invalidateCache('reviews');
              refreshUnifiedData();
            });
        } else if (item.type === 'note') {
          supabase
            .from('notes')
            .update({ custom_tags: newTags })
            .eq('id', item.id)
            .then(async () => {
              await invalidateCache('notes');
              refreshUnifiedData();
            });
        } else if (item.type === 'card') {
          const cardData = item.card as CareerCard;
          const updatedCardData = {
            ...cardData.card_data,
            custom_tags: newTags
          };
          
          supabase
            .from('career_cards')
            .update({ card_data: updatedCardData })
            .eq('cardid', item.id)
            .then(async () => {
              await invalidateCache('career-cards');
              refreshUnifiedData();
            });
        }
      });

    } catch (error) {
      console.error('Error toggling archive status:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>{'Failed to update archive status'}</div>
        </div>,
        'error'
      );
    }
  };

  // Move this useEffect up with other useEffects, before any conditional rendering
  useEffect(() => {
    let mounted = true;

    const initializeData = async () => {
      if (!mounted) return;
      
      const dataLoadStartTime = performance.now();
      
      try {
        await handleNetworkError(async () => {
          if (!mounted) return;
          
          // Wrap any existing API calls here
          // For example:
          if (cachedReviews) {
            setReviews(cachedReviews);
          }
          if (cachedNotes) {
            setNotes(cachedNotes);
          }
          if (cachedCards) {
            setCareerCards(cachedCards);
          }
        });
        
        // Track successful data load
        trackFeatureUsage('home_data_load', 'complete', {
          load_time_ms: Math.round(performance.now() - dataLoadStartTime),
          first_paint_ms: Math.round(
            performance.getEntriesByType('paint')
              .find(entry => entry.name === 'first-paint')?.startTime || performance.now()
          )
        });
        
        // Complete journey step
        trackUserJourney('data_loaded', {
          reviews_count: cachedReviews?.length || 0,
          notes_count: cachedNotes?.length || 0,
          cards_count: cachedCards?.length || 0
        });
      } catch (error) {
        if (!mounted) return;
        console.error('Failed to initialize data:', error);
        
        // Track failed data load
        trackFeatureUsage('home_data_load', 'error', {
          load_time_ms: Math.round(performance.now() - dataLoadStartTime),
          first_paint_ms: Math.round(
            performance.getEntriesByType('paint')
              .find(entry => entry.name === 'first-paint')?.startTime || performance.now()
          )
        });
        
        // Record error in journey
        trackUserJourney('data_load_error', {
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    };

    initializeData();

    return () => {
      mounted = false;
    };
  }, [cachedReviews, cachedNotes, cachedCards]); // Add dependencies that are used

  // Loading and error states will be handled in the main render

  // Add this function to handle new item creation
  const handleNewItemClick = (type: 'note' | 'card' | 'review') => {
    // Track feature usage
    trackFeatureUsage(`create_${type}_button`, 'click');
    
    // Track journey for item creation
    trackUserJourney('creation_started', {
      item_type: type
    });
    
    if (hasAnyUnsavedChanges()) {
      const timestamp = Temporal.Now.zonedDateTimeISO().toString().replace(/\[.*\]/, '');
      
      setTargetItem({
        type,
        id: '',
        title: '',
        content: '',
        custom_tags: ['Active'],
        date_tags: [],
        is_pinned: false,
        ...(type === 'note' ? {
          note_date: timestamp,
          created_at: timestamp,
          career_impact: 3,
          engagement_level: 3
        } : type === 'review' ? {
          review_date: timestamp,
          created_at: timestamp
        } : {
          created_at: timestamp
        })
      });
      setShowUnsavedDialog(true);
      return;
    }

    // If no unsaved changes, proceed with creating new item
    const timestamp = Temporal.Now.zonedDateTimeISO().toString().replace(/\[.*\]/, '');
    if (type === 'note') {
      setSelectedNote({
        id: '',
        title: '',
        content: '',
        custom_tags: ['Active'],
        date_tags: [],
        type: 'note',
        note_date: timestamp,
        created_at: timestamp,
        career_impact: 3,
        engagement_level: 3
      });
    } else if (type === 'card') {
      setIsCardTypeModalOpen(true);
    } else if (type === 'review') {
      setIsNewReviewModalOpen(true);
    }
  };

  // Update the create dropdown JSX to add the ref and keyboard navigation
  const renderCreateDropdown = () => (
    <div 
      className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
      role="menu"
      onKeyDown={(e) => {
        // Prevent scrolling when using arrow keys
        if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
          e.preventDefault();
        }
      }}
    >
      <div className="py-1" role="none">
        <button
          ref={newNoteButtonRef}
          onClick={() => {
            setIsCreateDropdownOpen(false);
            handleNewItemClick('note');
          }}
          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700"
          role="menuitem"
          onKeyDown={(e) => {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                const nextButton = e.currentTarget.parentElement?.querySelector('button:nth-child(2)');
                if (nextButton instanceof HTMLButtonElement) nextButton.focus();
                break;
              case 'ArrowUp':
                e.preventDefault();
                const lastButton = e.currentTarget.parentElement?.querySelector('button:last-child');
                if (lastButton instanceof HTMLButtonElement) lastButton.focus();
                break;
            }
          }}
        >
          <IoPencilOutline className="mr-2" size={16} />
          New Note
        </button>
        <button
          onClick={() => {
            setIsCreateDropdownOpen(false);
            handleNewItemClick('card');
          }}
          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700"
          role="menuitem"
          onKeyDown={(e) => {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                const nextButton = e.currentTarget.parentElement?.querySelector('button:last-child');
                if (nextButton instanceof HTMLButtonElement) nextButton.focus();
                break;
              case 'ArrowUp':
                e.preventDefault();
                const prevButton = e.currentTarget.parentElement?.querySelector('button:first-child');
                if (prevButton instanceof HTMLButtonElement) prevButton.focus();
                break;
            }
          }}
        >
          <IoDocumentTextOutline className="mr-2" size={16} />
          New Career Card
        </button>
        <button
          onClick={() => {
            setIsCreateDropdownOpen(false);
            handleNewItemClick('review');
          }}
          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700"
          role="menuitem"
          onKeyDown={(e) => {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                const firstButton = e.currentTarget.parentElement?.querySelector('button:first-child');
                if (firstButton instanceof HTMLButtonElement) firstButton.focus();
                break;
              case 'ArrowUp':
                e.preventDefault();
                const prevButton = e.currentTarget.parentElement?.querySelector('button:nth-child(2)');
                if (prevButton instanceof HTMLButtonElement) prevButton.focus();
                break;
            }
          }}
        >
          <IoSparklesOutline className="mr-2" size={16} />
          New Review
        </button>
      </div>
    </div>
  );

  // Add this helper function near getSelectedItemsCounts
  const getSelectedNotesOnly = (selectedItems: SelectedItems, items: CombinedItem[] | GroupedItems) => {
    const allItems = Array.isArray(items) ? items : Object.values(items).flat();
    return allItems.filter(item => selectedItems[item.id] && item.type === 'note') as Note[];
  };

  // Add this helper function near getSelectedItemsCounts
  const areAllSelectedItemsNotes = (selectedItems: SelectedItems, items: CombinedItem[] | GroupedItems) => {
    const allItems = Array.isArray(items) ? items : Object.values(items).flat();
    const selectedItemsList = allItems.filter(item => selectedItems[item.id]);
    return selectedItemsList.length > 0 && selectedItemsList.every(item => item.type === 'note');
  };

  // Add batch update functions
  const handleBatchCareerImpactUpdate = async (newLevel: number) => {
    const selectedNotes = getSelectedNotesOnly(selectedItems, processedItems);
    
    try {
      // Update local state immediately
      setNotes(prev => {
        const updated = [...prev];
        selectedNotes.forEach(note => {
          const index = updated.findIndex(n => n.id === note.id);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              career_impact: newLevel
            };
          }
        });
        return updated;
      });

      // Update in database
      const { error } = await supabase
        .from('notes')
        .update({ career_impact: newLevel })
        .in('id', selectedNotes.map(note => note.id));

      if (error) throw error;

      // Clear selection
      setSelectedItems({});

      // Show success toast
      toast.showToast(
        <div>
          <div className="font-medium">Success</div>
          <div>Updated career impact to "{IMPACT_LEVELS[newLevel as keyof typeof IMPACT_LEVELS].label}" for {selectedNotes.length} note{selectedNotes.length === 1 ? '' : 's'}</div>
        </div>,
        'success'
      );
    } catch (error) {
      console.error('Error updating career impact:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>Failed to update career impact</div>
        </div>,
        'error'
      );
    }
  };

  const handleBatchEngagementLevelUpdate = async (newLevel: number) => {
    const selectedNotes = getSelectedNotesOnly(selectedItems, processedItems);
    
    try {
      // Update local state immediately
      setNotes(prev => {
        const updated = [...prev];
        selectedNotes.forEach(note => {
          const index = updated.findIndex(n => n.id === note.id);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              engagement_level: newLevel
            };
          }
        });
        return updated;
      });

      // Update in database
      const { error } = await supabase
        .from('notes')
        .update({ engagement_level: newLevel })
        .in('id', selectedNotes.map(note => note.id));

      if (error) throw error;

      // Clear selection
      setSelectedItems({});

      // Show success toast
      toast.showToast(
        <div>
          <div className="font-medium">Success</div>
          <div>Updated engagement level to "{ENGAGEMENT_LEVELS[newLevel as keyof typeof ENGAGEMENT_LEVELS].label}" for {selectedNotes.length} note{selectedNotes.length === 1 ? '' : 's'}</div>
        </div>,
        'success'
      );
    } catch (error) {
      console.error('Error updating engagement level:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>Failed to update engagement level</div>
        </div>,
        'error'
      );
    }
  };

  // Add batch update functions for career cards
  const handleBatchCareerCardImpactUpdate = async (newLevel: number) => {
    const selectedCards = getSelectedCardsOnly(selectedItems, processedItems);
    // Create timestamp with timezone offset
    const now = new Date();
    const timeZoneOffset = now.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(timeZoneOffset / 60));
    const offsetMinutes = Math.abs(timeZoneOffset % 60);
    const offsetSign = timeZoneOffset <= 0 ? '+' : '-';
    const timestamp = `${now.toISOString().slice(0, 19)}${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Update local state immediately
      setCareerCards(prev => {
        const updated = [...prev];
        selectedCards.forEach(card => {
          const index = updated.findIndex(c => c.cardid === card.cardid);
          if (index !== -1) {
            const newMetric = {
              value: newLevel,
              last_updated: timestamp
            };
            
            // Update the metric at root level
            updated[index] = {
              ...updated[index],
              career_impact: newMetric
            };

            // Add to impact history in card_data
            const currentHistory = updated[index].card_data.impact_history?.entries || [];
            const newHistoryEntry = {
              career_impact: newLevel,
              engagement_level: updated[index].engagement_level.value,
              timestamp
            };

            updated[index].card_data = {
              ...updated[index].card_data,
              impact_history: {
                entries: [...currentHistory, newHistoryEntry]
              }
            };
          }
        });
        return updated;
      });

      // Update in database
      const updates = selectedCards.map(card => ({
        cardid: card.cardid,
        user_id: user.id,
        career_impact: {
          value: newLevel,
          last_updated: timestamp
        },
        card_data: {
          ...card.card_data,
          impact_history: {
            entries: [
              ...(card.card_data.impact_history?.entries || []),
              {
                career_impact: newLevel,
                engagement_level: card.engagement_level.value,
                timestamp
              }
            ]
          }
        }
      }));

      const { error } = await supabase
        .from('career_cards')
        .upsert(updates, { onConflict: 'cardid' });

      if (error) throw error;

      // Clear selection
      setSelectedItems({});

      // Show success toast
      toast.showToast(
        <div>
          <div className="font-medium">Success</div>
          <div>Updated career impact to "{IMPACT_LEVELS[newLevel as keyof typeof IMPACT_LEVELS].label}" for {selectedCards.length} card{selectedCards.length === 1 ? '' : 's'}</div>
        </div>,
        'success'
      );
    } catch (error) {
      console.error('Error updating career impact:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>Failed to update career impact</div>
        </div>,
        'error'
      );
    }
  };

  const handleBatchCareerCardEngagementLevelUpdate = async (newLevel: number) => {
    const selectedCards = getSelectedCardsOnly(selectedItems, processedItems);
    // Create timestamp with timezone offset
    const now = new Date();
    const timeZoneOffset = now.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(timeZoneOffset / 60));
    const offsetMinutes = Math.abs(timeZoneOffset % 60);
    const offsetSign = timeZoneOffset <= 0 ? '+' : '-';
    const timestamp = `${now.toISOString().slice(0, 19)}${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Update local state immediately
      setCareerCards(prev => {
        const updated = [...prev];
        selectedCards.forEach(card => {
          const index = updated.findIndex(c => c.cardid === card.cardid);
          if (index !== -1) {
            const newMetric = {
              value: newLevel,
              last_updated: timestamp
            };
            
            // Update the metric at root level
            updated[index] = {
              ...updated[index],
              engagement_level: newMetric
            };

            // Add to impact history in card_data
            const currentHistory = updated[index].card_data.impact_history?.entries || [];
            const newHistoryEntry = {
              career_impact: updated[index].career_impact.value,
              engagement_level: newLevel,
              timestamp
            };

            updated[index].card_data = {
              ...updated[index].card_data,
              impact_history: {
                entries: [...currentHistory, newHistoryEntry]
              }
            };
          }
        });
        return updated;
      });

      // Update in database
      const updates = selectedCards.map(card => ({
        cardid: card.cardid,
        user_id: user.id,
        engagement_level: {
          value: newLevel,
          last_updated: timestamp
        },
        card_data: {
          ...card.card_data,
          impact_history: {
            entries: [
              ...(card.card_data.impact_history?.entries || []),
              {
                career_impact: card.career_impact.value,
                engagement_level: newLevel,
                timestamp
              }
            ]
          }
        }
      }));

      const { error } = await supabase
        .from('career_cards')
        .upsert(updates, { onConflict: 'cardid' });

      if (error) throw error;

      // Clear selection
      setSelectedItems({});

      // Show success toast
      toast.showToast(
        <div>
          <div className="font-medium">Success</div>
          <div>Updated engagement level to "{ENGAGEMENT_LEVELS[newLevel as keyof typeof ENGAGEMENT_LEVELS].label}" for {selectedCards.length} card{selectedCards.length === 1 ? '' : 's'}</div>
        </div>,
        'success'
      );
    } catch (error) {
      console.error('Error updating engagement level:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>Failed to update engagement level</div>
        </div>,
        'error'
      );
    }
  };

  const areAllSelectedItemsCareerCards = (
    selectedItems: Record<string, boolean>,
    items: CombinedItem[] | GroupedItems
  ): boolean => {
    // Convert items to array if it's grouped
    const itemsArray = Array.isArray(items) 
      ? items 
      : Object.values(items).flat();

    // Get selected items
    const selectedItemsArray = itemsArray.filter(item => selectedItems[item.id]);

    // Check if we have any selected items and they are all career cards
    return selectedItemsArray.length > 0 && selectedItemsArray.every(item => 
      item.type === 'card' && 'card' in item && item.card !== undefined
    );
  };

  const getSelectedCardsOnly = (
    selectedItems: Record<string, boolean>,
    items: CombinedItem[] | GroupedItems
  ): CareerCard[] => {
    // Convert items to array if it's grouped
    const itemsArray = Array.isArray(items) 
      ? items 
      : Object.values(items).flat();

    // Filter for selected career cards and map to CareerCard type
    return itemsArray
      .filter(item => selectedItems[item.id] && item.type === 'card' && 'card' in item && item.card !== undefined)
      .map(item => item.card as CareerCard);
  };

  // Add unsaved changes dialog handler
  const handleUnsavedDialogAction = async (action: 'save' | 'discard' | 'cancel') => {
    try {
      if (action === 'cancel') {
        setShowUnsavedDialog(false);
        setTargetItem(null);
        return;
      }

      if (action === 'save' && targetItem) {
        // Save the current item based on its type
        if (noteEditState) {
          await handleNoteSave(noteEditState.currentContent);
        } else if (reviewEditState) {
          await handleReviewSave(reviewEditState.currentContent);
        } else if (cardEditState) {
          await handleCardSave(cardEditState.currentContent);
        }
      }

      // Clear edit states and handle the target item
      clearAllEditStates();
      if (targetItem) {
        if (targetItem.id) {
          // If target item has an ID, it's an existing item to switch to
          switchToItem(targetItem);
        } else {
          // If target item has no ID, it's a new item to create
          if (targetItem.type === 'note') {
            setSelectedNote(targetItem as Note);
          } else if (targetItem.type === 'card') {
            setIsCardTypeModalOpen(true);
          } else if (targetItem.type === 'review') {
            setIsNewReviewModalOpen(true);
          }
        }
        setTargetItem(null);
      }
      setShowUnsavedDialog(false);
    } catch (error) {
      console.error('Error handling unsaved dialog action:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Add pagination controls component
  const PaginationControls = () => {
    const handlePageChange = (newPage: number) => {
      setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
    };

    const handleItemsPerPageChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newItemsPerPage = parseInt(event.target.value);
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Reset to first page when changing items per page
      await saveUserSettings({ itemsPerPage: newItemsPerPage });
    };

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="form-select rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            aria-label="Items per page"
          >
            <option value="10">10 / page</option>
            <option value="20">20 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Add this function to fetch active to-dos and follow-ups
  const fetchActiveTasks = async () => {
    setIsLoadingToDos(true);
    try {
      // Fetch active to-dos from Project cards
      const { data: todoCards, error: todoError } = await supabase
        .from('career_cards')
        .select('cardid, card_data')
        .eq('user_id', user.id)
        .eq('card_data->>type', 'Project')
        .not('card_data->active_to_do', 'eq', '[]')
        .not('card_data->active_to_do', 'is', null);

      if (todoError) throw todoError;
      
      // Fetch active follow-ups from People cards
      const { data: followUpCards, error: followUpError } = await supabase
        .from('career_cards')
        .select('cardid, card_data')
        .eq('user_id', user.id)
        .eq('card_data->>type', 'People')
        .not('card_data->active_follow_ups', 'eq', '[]')
        .not('card_data->active_follow_ups', 'is', null);

      if (followUpError) throw followUpError;

      // Process to-do cards
      const processedToDos = (todoCards || []).map(card => ({
        card: {
          id: card.cardid,
          title: card.card_data.title || 'Untitled Card',
          type: card.card_data.type
        },
        todos: Array.isArray(card.card_data.active_to_do) ? card.card_data.active_to_do.map((todo: any) => ({
          ...todo,
          card_id: card.cardid
        })) : []
      })).filter(item => item.todos.length > 0);

      // Process follow-up cards
      const processedFollowUps = (followUpCards || []).map(card => ({
        card: {
          id: card.cardid,
          title: card.card_data.title || 'Untitled Card',
          type: card.card_data.type
        },
        followUps: Array.isArray(card.card_data.active_follow_ups) ? card.card_data.active_follow_ups.map((followUp: any) => ({
          ...followUp,
          card_id: card.cardid
        })) : []
      })).filter(item => item.followUps.length > 0);

      setActiveToDos(processedToDos);
      setActiveFollowUps(processedFollowUps);
    } catch (error) {
      console.error('Error fetching active tasks:', error);
      toast.showToast(
        <div>
          <div className="font-medium">Error</div>
          <div>Failed to load active tasks</div>
        </div>,
        'error'
      );
    } finally {
      setIsLoadingToDos(false);
    }
  };

  // Open the to-do modal and fetch the tasks
  const handleOpenToDoModal = () => {
    setIsToDoModalOpen(true);
    fetchActiveTasks();
  };

  // Handle navigating to a card
  const handleNavigateToCard = (cardId: string) => {
    // Find the card in our career cards
    const cardItem = careerCards.find(card => card.cardid === cardId);
    if (cardItem) {
      setSelectedCard(cardItem);
      setIsToDoModalOpen(false);
    } else {
      // If not in local state, fetch it
      supabase
        .from('career_cards')
        .select('*')
        .eq('cardid', cardId)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error('Error fetching card:', error);
            return;
          }
          if (data) {
            setSelectedCard(data as CareerCard);
            setIsToDoModalOpen(false);
          }
        });
    }
  };

  // Render the to-do modal
  const renderToDoModal = () => (
    <ToDoModal
      isOpen={isToDoModalOpen}
      onClose={() => setIsToDoModalOpen(false)}
      activeToDos={activeToDos}
      activeFollowUps={activeFollowUps}
      isLoading={isLoadingToDos}
      onCardClick={handleNavigateToCard}
    />
  );

  // Monitor offline operations and network status
  useEffect(() => {
    const updateOfflineStatus = () => {
      setIsOffline(!navigator.onLine);
      const count = getOfflineOperationsCount();
      setOfflineOpsCount(count);
    };

    const handleOnline = () => {
      setIsOffline(false);
      // Trigger sync when coming back online
      syncOfflineOperations().then(results => {
        if (results.synced > 0) {
          toast.showToast(
            `✅ Synced ${results.synced} offline operations`,
            'success'
          );
        }
      });
    };

    const handleOffline = () => {
      setIsOffline(true);
      toast.showToast(
        '📱 You\'re offline. Changes will sync when reconnected.',
        'info'
      );
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Update count periodically
    const interval = setInterval(updateOfflineStatus, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [getOfflineOperationsCount, syncOfflineOperations, toast]);

  // Add helper function to open XP modal
  const handleOpenXPModal = () => {
    window.dispatchEvent(new CustomEvent('openXPModal'));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Show loading state */}
      {unifiedLoading && !cachedReviews && !cachedNotes && !cachedCards ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : unifiedError ? (
        /* Show error state */
        <div className="flex items-center justify-center h-full text-red-500">
          <p>Error loading data. Please try again later.</p>
        </div>
      ) : (
        <>
          <PageHeader
        title="Home"
        primaryButton={
          isMobile && (selectedReview || selectedNote || selectedCard) ? (
            <button
              onClick={() => {
                setSelectedReview(null);
                setSelectedNote(null);
                setSelectedCard(null);
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
              aria-label="Back to list"
            >
              <IoChevronForwardOutline className="rotate-180" size={20} />
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              {/* Career Focus Mode Toggle */}
              {careerFocusFeatures.careerFocus && (
                <FocusModeSlider />
              )}
              {/* Offline indicator */}
              {(isOffline || offlineOpsCount > 0) && (
                <div className="flex items-center space-x-1 px-2 py-1 rounded-md text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                  {isOffline && <span>📱</span>}
                  {offlineOpsCount > 0 && <span>{offlineOpsCount} pending</span>}
                </div>
              )}
              
              <button
                onClick={handleOpenToDoModal}
                className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                title="View active to-dos and follow-ups"
              >
                <div className="flex items-center">
                  <IoListOutline className="mr-1" size={16} />
                  To Do List
                </div>
              </button>
              <div className="relative" ref={createDropdownRef}>
                <button
                  onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                  title="Create new item (Ctrl/Cmd + M)"
                >
                  <IoAddOutline size={20} />
                </button>
                {isCreateDropdownOpen && renderCreateDropdown()}
              </div>
            </div>
          )
        }
      />
      {/* Render the to-do modal */}
      {renderToDoModal()}
      <div className="flex-1 overflow-hidden relative" style={{ paddingTop: `${PAGE_HEADER_HEIGHT}px` }}>
        <div className="h-full flex">
          {/* List Panel */}
          <div
            style={{ width: !isMobile ? `${listWidth}px` : undefined }}
            className={`h-full overflow-y-auto border-r border-gray-200 dark:border-gray-700 ${
              isMobile
                ? selectedReview || selectedNote || selectedCard
                  ? 'w-0 border-r-0'
                  : 'w-full'
                : ''
            }`}
          >
            <div className={`${isMobile && (selectedReview || selectedNote || selectedCard) ? 'hidden' : ''}`}>
              <div className="sticky top-0 bg-white dark:bg-gray-800 px-1 py-2 border-b border-gray-200 dark:border-gray-700 z-10">
                {/* Search and view mode row */}
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Search reviews and notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="grow p-2 rounded-lg border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                  />
                  <ReviewsSettings
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                    scrollMode={scrollMode}
                    onScrollModeChange={handleScrollModeChange}
                  />
                </div>

                {/* Filter section */}
                <div className="flex flex-col gap-2">
                  {/* Tags and sort row */}
                  <div className="flex items-center gap-2">
                    <div className="grow">
                      <TagsDropdown
                        tags={allTags}
                        selectedTags={selectedFilterTags}
                        onTagsChange={setSelectedFilterTags}
                        typeVisibility={typeVisibility}
                        onTypeVisibilityChange={handleTypeVisibilityChange}
                        showTypeSliders={true}
                      />
                    </div>
                    <div className="relative group shrink-0" ref={sortDropdownRef}>
                      <button
                        onClick={() => setIsSortOpen(!isSortOpen)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Sort options"
                      >
                        <IoFunnelOutline size={20} />
                      </button>
                      {isSortOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                          <div className="p-2">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Sort By
                            </div>
                            <button
                              onClick={() => {
                                handleSortChange('date');
                                setIsSortOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-md ${sortBy === 'date'
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              Date
                            </button>
                            <button
                              onClick={() => {
                                handleSortChange('title');
                                setIsSortOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-md ${sortBy === 'title'
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              Title
                            </button>
                            <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={() => {
                                handleGroupByChange(!groupByType);
                                setIsSortOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between ${groupByType
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <span>Group by Type</span>
                              {groupByType && <IoCheckmarkOutline size={16} />}
                            </button>
                            <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={() => {
                                setShowArchived(!showArchived);
                                setIsSortOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between ${
                                showArchived
                                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <span>{showArchived ? 'Show Active' : 'Show Archived'}</span>
                              {showArchived && <IoCheckmarkOutline size={16} />}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selection menu - now full width */}
                  {Object.keys(selectedItems).length > 0 && (
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-sm">
                      <div className="group relative">
                        <span className="text-blue-700 dark:text-blue-300 cursor-help">
                          {Object.keys(selectedItems).length} selected
                        </span>
                        {/* Tooltip */}
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                          <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-lg">
                            {(() => {
                              const counts = getSelectedItemsCounts(selectedItems, processedItems);
                              return (
                                <>
                                  {counts.notes > 0 && <div>Notes: {counts.notes}</div>}
                                  {counts.careerCards > 0 && <div>Career Cards: {counts.careerCards}</div>}
                                  {counts.reviews > 0 && <div>Reviews: {counts.reviews}</div>}
                                </>
                              );
                            })()}
                          </div>
                          {/* Tooltip arrow */}
                          <div className="absolute left-4 top-full -mt-1.5 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-gray-900 dark:border-gray-100"></div>
                        </div>
                      </div>
                      
                      {/* Actions Dropdown */}
                      <div className="relative" ref={actionsDropdownRef}>
                        <button
                          onClick={() => setIsActionsOpen(!isActionsOpen)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
                        >
                          Actions
                          <IoChevronDownOutline size={14} className={`transform transition-transform ${isActionsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isActionsOpen && (
                          <div className="absolute left-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                            <button
                              onClick={handleBulkArchiveToggle}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              {areAllSelectedItemsArchived(selectedItems, processedItems) ? (
                                <>
                                  <IoArchiveOutline className="rotate-180" size={16} />
                                  Unarchive
                                </>
                              ) : (
                                <>
                                  <IoArchiveOutline size={16} />
                                  Archive
                                </>
                              )}
                            </button>

                            {/* Only show impact metric actions if all selected items are notes */}
                            {areAllSelectedItemsNotes(selectedItems, processedItems) && (
                              <>
                                {/* Career Impact submenu */}
                                <div className="relative group">
                                  <button 
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                    onClick={() => {
                                      setIsCareerImpactOpen(!isCareerImpactOpen);
                                      setIsEngagementLevelOpen(false);
                                    }}
                                  >
                                    <span>Career Impact</span>
                                    <IoChevronForwardOutline size={14} />
                                  </button>
                                  {isCareerImpactOpen && (
                                    <div 
                                      className="absolute left-full top-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 ml-1"
                                      onMouseLeave={() => setIsCareerImpactOpen(false)}
                                    >
                                      {Object.entries(IMPACT_LEVELS).map(([level, { label }]) => (
                                        <button
                                          key={level}
                                          onClick={() => handleBatchCareerImpactUpdate(parseInt(level))}
                                          className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Engagement Level submenu */}
                                <div className="relative group">
                                  <button 
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                    onClick={() => {
                                      setIsEngagementLevelOpen(!isEngagementLevelOpen);
                                      setIsCareerImpactOpen(false);
                                    }}
                                  >
                                    <span>Engagement Level</span>
                                    <IoChevronForwardOutline size={14} />
                                  </button>
                                  {isEngagementLevelOpen && (
                                    <div 
                                      className="absolute left-full top-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 ml-1"
                                      onMouseLeave={() => setIsEngagementLevelOpen(false)}
                                    >
                                      {Object.entries(ENGAGEMENT_LEVELS).map(([level, { label }]) => (
                                        <button
                                          key={level}
                                          onClick={() => handleBatchEngagementLevelUpdate(parseInt(level))}
                                          className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                            {areAllSelectedItemsCareerCards(selectedItems, processedItems) && (
                              <>
                                {/* Career Impact submenu */}
                                <div className="relative group">
                                  <button 
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                    onClick={() => {
                                      setIsCareerImpactOpen(!isCareerImpactOpen);
                                      setIsEngagementLevelOpen(false);
                                    }}
                                  >
                                    <span>Career Impact</span>
                                    <IoChevronForwardOutline size={14} />
                                  </button>
                                  {isCareerImpactOpen && (
                                    <div 
                                      className="absolute left-full top-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 ml-1"
                                      onMouseLeave={() => setIsCareerImpactOpen(false)}
                                    >
                                      {Object.entries(IMPACT_LEVELS).map(([level, { label }]) => (
                                        <button
                                          key={level}
                                          onClick={() => handleBatchCareerCardImpactUpdate(parseInt(level))}
                                          className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Engagement Level submenu */}
                                <div className="relative group">
                                  <button 
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                    onClick={() => {
                                      setIsEngagementLevelOpen(!isEngagementLevelOpen);
                                      setIsCareerImpactOpen(false);
                                    }}
                                  >
                                    <span>Engagement Level</span>
                                    <IoChevronForwardOutline size={14} />
                                  </button>
                                  {isEngagementLevelOpen && (
                                    <div 
                                      className="absolute left-full top-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 ml-1"
                                      onMouseLeave={() => setIsEngagementLevelOpen(false)}
                                    >
                                      {Object.entries(ENGAGEMENT_LEVELS).map(([level, { label }]) => (
                                        <button
                                          key={level}
                                          onClick={() => handleBatchCareerCardEngagementLevelUpdate(parseInt(level))}
                                          className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={handleDeselectAll}
                        className="ml-auto text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        Deselect All
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Show onboarding banner in mobile list view when onboarding not complete */}
              {isMobile && user && !onboardingComplete && (
                <div className="px-4 py-2">
                  <OnboardingBanner />
                </div>
              )}
              {/* Show database banner notifications in mobile list view */}
              {isMobile && (
                <div className="px-4 py-2">
                  <DatabaseBanner placement="home-mobile" onOpenXPModal={handleOpenXPModal} />
                </div>
              )}
              <div className="px-1">
                {renderNotes()}
              </div>
            </div>
          </div>

          {/* Resize Handle - Only show on desktop */}
          {!isMobile && (
            <div
              className="w-2 h-full flex items-center justify-center cursor-col-resize hover:bg-gray-100 dark:hover:bg-gray-700 z-50"
              onMouseDown={(e) => {
                e.preventDefault();
                document.body.style.cursor = 'col-resize';

                const startX = e.pageX;
                const startWidth = listWidth;

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  const newWidth = startWidth + (moveEvent.pageX - startX);
                  const maxWidth = window.innerWidth - 320; // Ensure detail panel has at least 320px
                  setListWidth(Math.min(Math.max(320, newWidth), maxWidth));
                };

                const handleMouseUp = () => {
                  document.body.style.cursor = 'default';
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
            >
              <div className="w-px h-20 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
          )}

          {/* Detail Panel */}
          <div 
            ref={scrollContainerRef}
            className={`h-full overflow-y-auto bg-gray-50 dark:bg-gray-900 ${
              isMobile
                ? selectedReview || selectedNote || selectedCard
                  ? 'w-full'
                  : 'w-0 hidden'
                : 'flex-1 min-w-[320px]'
            }`}
            style={{
              // Add padding-bottom when the URL bar is visible on mobile
              paddingBottom: isMobile ? '120px' : undefined
            }}
          >
            <AnimatePresence>
              {selectedReview && (
                <UnifiedSidePanel
                  item={{
                    ...selectedReview,
                    type: 'review' as const,
                    title: selectedReview.review_name,
                    content: selectedReview.review_text,
                  }}
                  onClose={() => {
                    setSelectedReview(null);
                    clearAllEditStates();
                  }}
                  onSave={async (item) => {
                    if (item.type !== 'review') throw new Error('Invalid item type');
                    const savedReview = await handleReviewSave({
                      ...selectedReview,
                      review_name: item.title,
                      review_text: item.content,
                      custom_tags: item.custom_tags,
                      date_tags: item.date_tags,
                      review_date: item.review_date,
                    });
                    setReviewEditState(null);
                    return savedReview;
                  }}
                  onDelete={handleReviewDelete}
                  skipAnimation={wasPanelOpen}
                  editState={reviewEditState as any}
                  setEditState={setReviewEditState as any}
                />
              )}
              {selectedNote && (
                <UnifiedSidePanel
                  item={{
                    ...selectedNote,
                    type: 'note' as const,
                  }}
                  onClose={() => {
                    setSelectedNote(null);
                    clearAllEditStates();
                  }}
                  onSave={async (item) => {
                    if (item.type !== 'note') throw new Error('Invalid item type');
                    const savedNote = await handleNoteSave({
                      ...selectedNote,
                      title: item.title,
                      content: item.content,
                      custom_tags: item.custom_tags,
                      date_tags: item.date_tags,
                      note_date: item.note_date,
                      career_impact: item.career_impact,
                      engagement_level: item.engagement_level,
                    });
                    setNoteEditState(null);
                    return savedNote;
                  }}
                  onDelete={handleNoteDelete}
                  startInEditMode={!selectedNote.id}
                  skipAnimation={wasPanelOpen}
                  editState={noteEditState as any}
                  setEditState={setNoteEditState as any}
                />
              )}
              {selectedCard && (
                <CareerCardPanel
                  card={selectedCard}
                  onClose={() => {
                    setSelectedCard(null);
                    clearAllEditStates();
                  }}
                  onSave={async (card) => {
                    const savedCard = await handleCardSave(card as any);
                    setCardEditState(null);
                    return savedCard;
                  }}
                  onDelete={handleCardDelete}
                  startInEditMode={!selectedCard.cardid}
                  skipAnimation={wasPanelOpen}
                  editState={cardEditState as any}
                  setEditState={setCardEditState as any}
                  onCardChange={(updatedCard) => {
                    setSelectedCard(updatedCard);
                  }}
                  isEditing={cardEditState?.isEditing || false}
                  setIsEditing={(isEditing) => {
                    if (cardEditState) {
                      setCardEditState({ ...cardEditState, isEditing });
                    }
                  }}
                  onSaveCard={async (card) => {
                    await handleCardSave(card as any);
                  }}
                  cardTypes={[]}
                />
              )}
              {!selectedReview && !selectedNote && !selectedCard && (
                // Empty State
                (<div className="w-full h-full flex flex-col items-center justify-start p-8 pt-8">
                  <div className="w-full max-w-7xl mx-auto">
                    <h3 className="text-2xl font-semibold mb-4 text-center text-gray-800 dark:text-gray-100">
                      {uxFeatures.enhancedDashboard ? 'Welcome to Your Enhanced Career Dashboard' : 'Welcome to Career Card'}
                    </h3>
                    
                    {uxFeatures.enhancedDashboard && (
                      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-center">
                        <p className="text-blue-800 dark:text-blue-200">
                          🎉 You're experiencing our enhanced dashboard with improved navigation and features!
                        </p>
                      </div>
                    )}
                    
                    {user && !onboardingComplete && (
                      <OnboardingBanner className="mb-8" />
                    )}
                    
                    {/* Show database banner notifications in desktop detail panel */}
                    {!isMobile && <DatabaseBanner placement="home-desktop" className="mb-8" onOpenXPModal={handleOpenXPModal} />}
                    
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-8 auto-rows-[280px]">
                      {/* Notes Box */}
                      <div className="flex flex-col p-6 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all max-w-sm mx-auto w-full">
                        <div className="flex-1 min-h-0">
                          <h4 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Notes</h4>
                          <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Freeform thoughts and ideas</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Recap your latest work</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Personal reflections</span>
                            </li>
                          </ul>
                        </div>
                        <div className="pt-4 mt-auto">
                          <button
                            onClick={() => {
                              trackFeatureUsage('note_creation', 'click');
                              setIsCreateDropdownOpen(false);
                              setSelectedNote({
                                id: '',
                                title: '',
                                content: '',
                                custom_tags: ['Active'],
                                date_tags: [],
                                type: 'note',
                                note_date: new Date().toISOString(),
                                created_at: new Date().toISOString(),
                                career_impact: 3,
                                engagement_level: 3
                              });
                            }}
                            className="w-full px-4 py-2.5 text-white font-medium bg-green-500 hover:bg-green-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          >
                            Create Note
                          </button>
                        </div>
                      </div>

                      {/* Career Cards Box */}
                      <div className="flex flex-col p-6 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all max-w-sm mx-auto w-full">
                        <div className="flex-1 min-h-0">
                          <h4 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Career Cards</h4>
                          <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Structured Career documentation</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Feedback and interactions with colleagues and clients</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Goal and results tracking</span>
                            </li>
                          </ul>
                        </div>
                        <div className="pt-4 mt-auto">
                          <button
                            onClick={() => {
                              trackFeatureUsage('career_card_creation', 'click');
                              setIsCreateDropdownOpen(false);
                              setIsCardTypeModalOpen(true);
                            }}
                            className="w-full px-4 py-2.5 text-white font-medium bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          >
                            Create Card
                          </button>
                        </div>
                      </div>

                      {/* Reviews Box */}
                      <div className="flex flex-col p-6 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all max-w-sm mx-auto w-full">
                        <div className="flex-1 min-h-0">
                          <h4 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Reviews</h4>
                          <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Performance reviews</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>AI assisted & suggestions</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Synthesis of notes and cards</span>
                            </li>
                          </ul>
                        </div>
                        <div className="pt-4 mt-auto">
                          <button
                            onClick={() => {
                              trackFeatureUsage('review_creation', 'click');
                              setIsCreateDropdownOpen(false);
                              setIsNewReviewModalOpen(true);
                            }}
                            className="w-full px-4 py-2.5 text-white font-medium bg-purple-500 hover:bg-purple-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          >
                            Create Review
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Modals */}
      {isNewReviewModalOpen && (
        <ReviewModal
          isOpen={isNewReviewModalOpen}
          onClose={() => setIsNewReviewModalOpen(false)}
          onSubmit={handleReviewSubmit}
          initialNotes={(cachedNotes || []).map(transformToFilterItem)}
          initialCards={(cachedCards || []).map(transformToFilterItem)}
          isSubmitting={isCreatingReview}
        />
      )}
      {isCardTypeModalOpen && (
        <CardTypeModal
          isOpen={isCardTypeModalOpen}
          onClose={() => setIsCardTypeModalOpen(false)}
          onSubmit={handleCardTypeSelect as any}
        />
      )}
      {showUnsavedDialog && (
        <Modal
          isOpen={showUnsavedDialog}
          onClose={() => setShowUnsavedDialog(false)}
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Unsaved Changes</h2>
            <p className="mb-6">You have unsaved changes. What would you like to do?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => handleUnsavedDialogAction('cancel')}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUnsavedDialogAction('discard')}
                className="px-4 py-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-md"
              >
                Discard Changes
              </button>
              <button
                onClick={() => handleUnsavedDialogAction('save')}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md"
              >
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
        </>
      )}
    </div>
  );
};

export default Home;